/* ============================================================
   SC Will It Fit: page controller (v3)
   ------------------------------------------------------------
   The page side of the tool: the sofa picker, the route form,
   the answer and the email to the team. It holds no geometry.
   Every fit decision is made by SC.fitEngine (sc-fit-engine.js),
   which returns tiers, methods and numbers. This file turns
   those into sentences and never adjusts a tier upwards.

   Sofa dimensions come from SC.fitDimensions
   (sc-fit-dimensions.js, a generated copy of the SSOT feed). The
   page paints from that copy at once, then reads the live feed
   and repaints only if a figure changed, so a number corrected
   in the sheet reaches the tool without a theme push.

   Verdict tiers, best to worst (the engine's own):
     fit    : works with every clear dimension of the house
              shrunk by the safety margin (5 cm / 2 in)
     tight  : works as measured but not with the margin taken
              off, or is something we always look at ourselves
     onend  : a flat carry fails, but stood on end, leaned,
              tipped, or with fixed legs fed through a doorway,
              the sofa should go. Never a promise.
     team   : nothing passes cleanly, or the only carry that
              works has under 2 cm (0.75 in) to spare, which is
              too close to call. Never a flat no: a split frame,
              a door off its hinges or another way in may still
              work, so we route to the team.
   A blank measurement is "not checked yet", never "fine", and a
   figure filled in by a "closest match" preset is a typical
   size, never "your measurement".

   Public surface
   --------------
   SC.fitChecker.contactEmail, .dimensionsUrl, .ranges, .hideSizes
                        : set by the section before this file runs
   SC.fitChecker._test  : the pure functions (no DOM), used by
                          tools/test-fit-page.js and the local harness
   ============================================================ */
(function () {
  'use strict';

  var root = (typeof window !== 'undefined') ? window : {};
  var doc = (typeof document !== 'undefined') ? document : null;
  var SC = root.SC = root.SC || {};
  /* The section fills SC.fitChecker from an inline script that runs
     before this file, so extend the object, never replace it. */
  var FC = SC.fitChecker = SC.fitChecker || {};

  /* ============ 1. CONSTANTS ============ */
  var CM_PER_IN = 2.54;
  var NBSP = '\u00A0';
  var MARKET_UNIT = { UK: 'cm', US: 'in' };

  /* Copies of the engine's per-unit figures, for wording only. The
     margin is read from SC.fitEngine.constants() when the engine is
     there; these are the fallback. */
  var MARGIN = { cm: 5, 'in': 2 };
  /* The engine stops measuring spare room at this much (its SPARE_MAX),
     so anything at the cap is reported as "more than". */
  var SPARE_CAP = { cm: 60, 'in': 24 };
  /* Under this much spare the engine will not call a carry (its
     TIGHT_FLOOR), and a ceiling nobody measured is only taken on trust
     up to this height (its TYPICAL_CEILING). */
  var FLOOR = { cm: 2, 'in': 0.75 };
  var TYPICAL = { cm: 240, 'in': 96 };
  /* What lifting a door off its hinges usually gains. */
  var DOOR_OFF = { cm: 4, 'in': 1.5 };
  /* Longest mailto link we will build, counted after encoding. Windows
     hands the link to the mail client through a call that stops at
     2,083 characters, and a longer one fails without a word. */
  var URL_LIMIT = 2000;

  var DEFAULT_RANGES = 'Coco, Coco Sofa Bed, Barton, Isobel, Felix, Henrietta, Sophia, Cocobella';

  /* Only used when the feed carries no Frame Design (an older payload
     gives split: null). Keyed on the range key. */
  var SPLIT_FALLBACK = { coco: true, barton: true, isobel: true, felix: true, cocobella: true };

  var TIER_RANK = { fit: 0, tight: 1, unknown: 1, onend: 2, team: 3 };

  /* Route presets, in the market's own unit. These are CLEAR openings,
     not door leaf sizes: a 2ft 6in (762 mm) internal door leaves about
     72 cm between the stop and the open door. Value is "width,height"
     (hall: width,ceiling; stairs: width,headroom).
     The labels carry {w} and {h} where a figure goes, never a typed
     number: presetLabel() fills them in whichever unit the customer has
     selected, so a label can never state a unit the fields disagree
     with. A door's trade name (2ft 6in, 36 inch) is a name and stays. */
  var PRESETS = {
    UK: {
      front: [
        ['78,198', 'Standard front door (about {w} clear)'],
        ['72,198', 'Narrow front door (about {w} clear)'],
        ['90,205', 'Wide front door (about {w} clear)']
      ],
      hall: [
        ['90,240', 'Narrow hallway ({w} wide)'],
        ['105,240', 'Standard hallway ({w} wide)'],
        ['130,250', 'Generous hallway ({w} wide)']
      ],
      stair: [
        ['80,200', 'Narrow stairs ({w} clear, {h} headroom)'],
        ['86,200', 'Standard stairs ({w} clear, {h} headroom)'],
        ['95,200', 'Generous stairs ({w} clear, {h} headroom)']
      ],
      room: [
        ['72,198', 'Standard internal door, 2ft 6in (about {w} clear)'],
        ['67,198', 'Narrow internal door, 2ft 3in (about {w} clear)'],
        ['80,198', 'Wide internal door, 2ft 9in (about {w} clear)']
      ]
    },
    US: {
      front: [
        ['34,80', 'Standard 36 inch entry door (about {w} clear)'],
        ['30,80', 'Narrow 32 inch entry door (about {w} clear)'],
        ['40,80', 'Wide entry door (about {w} clear)']
      ],
      hall: [
        ['36,96', 'Narrow hallway ({w} wide)'],
        ['42,96', 'Standard hallway ({w} wide)'],
        ['48,108', 'Generous hallway ({w} wide)']
      ],
      stair: [
        ['31.5,80', 'Standard 36 inch stairs with a handrail ({w} clear, {h} headroom)'],
        ['34,80', 'Wider stairs ({w} clear, {h} headroom)'],
        ['40,80', 'Generous stairs ({w} clear, {h} headroom)']
      ],
      room: [
        ['28,80', 'Narrow 30 inch interior door (about {w} clear)'],
        ['30,80', 'Standard 32 inch interior door (about {w} clear)'],
        ['34,80', 'Wide 36 inch interior door (about {w} clear)']
      ]
    }
  };
  var PRESET_KEYS = { front: ['frontW', 'frontH'], hall: ['hallW', 'hallH'], stair: ['stairW', 'stairH'], room: ['roomW', 'roomH'] };

  /* Every numeric route input, by its data-key. The names are fixed:
     the section, the tests and the email all use them. */
  var NUM_KEYS = [
    'frontW', 'frontH', 'outsideW', 'outsideH',
    'hallW', 'hallPinch', 'hallH', 'hallLen', 'hallW2',
    'liftDoorW', 'liftDoorH', 'liftW', 'liftD', 'liftH',
    'stairW', 'stairH', 'landingD', 'landingW', 'landingC', 'topD', 'topC', 'upW', 'upH',
    'roomW', 'roomH', 'roomC'
  ];
  var CHECK_KEYS = ['outsideSteps', 'corner', 'lift', 'stairs'];
  var CHOICE_KEYS = ['frontEntry', 'frontOutside', 'stairFoot', 'stairTop', 'stairSide', 'roomEntry'];

  /* ============ 2. SMALL HELPERS ============ */
  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function posNum(v) { return (isNum(v) && v > 0) ? v : null; }
  function has(list, item) { return !!list && list.indexOf(item) !== -1; }
  function isArray(v) { return Object.prototype.toString.call(v) === '[object Array]'; }
  function tidy(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').replace(/^ | $/g, ''); }
  function lower(s) { return tidy(s).toLowerCase(); }
  function slug(s) { return lower(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function convert(v, from, to) {
    if (from === to) return v;
    return to === 'in' ? v / CM_PER_IN : v * CM_PER_IN;
  }

  /* Numbers print to one decimal at most, with a trailing ".0" dropped.
     Spare room rounds down and a shortfall rounds up, so a printed
     figure never flatters the fit. */
  function plain(v) { return String(Math.round(v * 10) / 10); }
  function down(v) { return String(Math.floor(v * 10 + 1e-6) / 10); }
  function up(v) { return String(Math.ceil(v * 10 - 1e-6) / 10); }
  function show(v, u) { return plain(v) + NBSP + u; }
  /* For the engine's own constants, which are exact: 0.75 in must not
     print as 0.8. */
  function showExact(v, u) { return String(Math.round(v * 100) / 100) + NBSP + u; }
  function showDown(v, u) { return down(v) + NBSP + u; }
  function showUp(v, u) { return up(v) + NBSP + u; }

  function engineFigure(unit, name, fallback) {
    var engine = SC.fitEngine;
    if (engine && typeof engine.constants === 'function') {
      try {
        var c = engine.constants(unit);
        if (c && isNum(c[name])) return c[name];
      } catch (e) { /* fall through to the copy above */ }
    }
    return fallback[unit] != null ? fallback[unit] : fallback.cm;
  }
  function marginOf(unit) { return engineFigure(unit, 'M', MARGIN); }
  function spareCap(unit) { return engineFigure(unit, 'SPARE_MAX', SPARE_CAP); }
  function floorOf(unit) { return engineFigure(unit, 'TIGHT_FLOOR', FLOOR); }
  function typicalCeiling(unit) { return engineFigure(unit, 'TYPICAL_CEILING', TYPICAL); }

  /* A converted figure rounds down. Every route input is a clear
     dimension of the house, so the smaller reading is the safe one. */
  function shown(v, from, to) { return from === to ? String(v) : down(convert(v, from, to)); }

  /* A preset's label with its figures written in the unit the customer
     is working in: from is the unit the preset is held in (the
     market's), to the unit on the fields. */
  function presetLabel(label, value, from, to) {
    var parts = String(value).split(',');
    function fig(i) {
      var x = parseFloat(parts[i]);
      return isNum(x) ? shown(x, from, to) + NBSP + to : '';
    }
    return String(label).replace(/\{w\}/g, fig(0)).replace(/\{h\}/g, fig(1));
  }

  /* What a preset writes into its fields, as { key: figure } in the
     unit on the fields. */
  function presetFill(market, name, value, to) {
    var out = {};
    var keys = PRESET_KEYS[name] || [];
    var parts = String(value).split(',');
    keys.forEach(function (key, i) {
      var x = parseFloat(parts[i]);
      if (isNum(x)) out[key] = parseFloat(shown(x, MARKET_UNIT[market] || 'cm', to));
    });
    return out;
  }

  /* ============ 3. SOFA DATA ============ */

  function parseRanges(text) {
    var list = isArray(text) ? text : String(text || DEFAULT_RANGES).split(',');
    var out = [];
    list.forEach(function (name) {
      name = lower(name);
      if (name && !has(out, name)) out.push(name);
    });
    return out;
  }

  /* One per line, "Market | Range | Size". A line starting with # is a
     note to whoever edits the setting and is skipped, as is any line
     without both dividers. */
  function parseHideList(text) {
    var lines = isArray(text) ? text : String(text || '').split(/\r?\n/);
    var out = [];
    lines.forEach(function (line) {
      line = tidy(line);
      if (!line || line.charAt(0) === '#') return;
      var parts = line.split('|');
      if (parts.length < 3) return;
      out.push({ market: lower(parts[0]), range: lower(parts[1]), size: lower(parts.slice(2).join('|')) });
    });
    return out;
  }

  function isHidden(hide, market, range, size) {
    var mk = lower(market);
    for (var i = 0; i < hide.length; i++) {
      var h = hide[i];
      if (h.market !== '*' && h.market !== mk) continue;
      if (h.range !== lower(range.name) && slug(h.range) !== range.key) continue;
      if (h.size === lower(size.label) || h.size === lower(size.key)) return true;
    }
    return false;
  }

  /* The model list for one market: which ranges, in which order, with
     which sizes. settings = { ranges, hideSizes } as the section passes
     them. A range named in the setting but absent from the data is
     skipped; a range in the data but not in the setting is not shown. */
  function buildModels(feed, market, settings) {
    settings = settings || {};
    var block = feed && feed.markets && feed.markets[market];
    var ranges = (block && isArray(block.ranges)) ? block.ranges : [];
    var hide = parseHideList(settings.hideSizes);
    var models = [];

    parseRanges(settings.ranges).forEach(function (wanted) {
      var r = null;
      for (var i = 0; i < ranges.length; i++) {
        if (ranges[i] && lower(ranges[i].name) === wanted) { r = ranges[i]; break; }
      }
      if (!r) return;

      var sizes = [];
      var legs = false;
      (isArray(r.sizes) ? r.sizes : []).forEach(function (s) {
        if (!s) return;
        var w = posNum(s.w), h = posNum(s.h), d = posNum(s.d);
        if (w == null || h == null || d == null) return;
        if (isHidden(hide, market, r, s)) return;
        var access = posNum(s.access);
        /* An access height below the frame height is how the sheet says
           "the legs come off". */
        if (access != null && access < h - 0.05) legs = true;
        /* Leg height: only used to feed fixed legs through a doorway. */
        sizes.push({
          key: String(s.key), label: String(s.label), w: w, h: h, d: d, access: access,
          feet: (isNum(s.feet) && s.feet >= 0) ? s.feet : null
        });
      });
      /* Every size hidden: the range has nothing to offer here. */
      if (!sizes.length) return;

      models.push({
        key: String(r.key),
        name: String(r.name),
        type: r.type || 'sofa',
        split: (r.split === true || r.split === false) ? r.split : !!SPLIT_FALLBACK[r.key],
        detachable: r.detachable === true,
        legsRemovable: legs,
        sizes: sizes
      });
    });
    return models;
  }

  /* Cheap identity for a model list, so a live feed that matches the
     bundled copy does not trigger a repaint. */
  function fingerprint(models) {
    return (models || []).map(function (m) {
      return [m.key, m.name, m.split ? 1 : 0, m.detachable ? 1 : 0, m.legsRemovable ? 1 : 0].join(':') + '=' +
        m.sizes.map(function (s) { return [s.key, s.label, s.w, s.h, s.d, s.access, s.feet].join(','); }).join(';');
    }).join('|');
  }

  /* The key the Worker gives a size label, so a row in the feed's
     "rejected" list (which carries the label as typed in the sheet) can
     be matched to the bundled size it refers to. */
  function sizeKeyOf(label) {
    var t = tidy(label).replace(/\s*\(new\)$/i, '');
    var m = /^(\d+(?:\.5)?)/.exec(t);
    if (m) return m[1] === '1.5' ? 'loveseat' : m[1] + '-seater';
    var low = t.toLowerCase();
    if (low.indexOf('armchair') === 0) {
      var rest = low.split('/')[1];
      return rest ? slug('armchair ' + rest) : 'armchair';
    }
    if (low.indexOf('loveseat') !== -1) return 'loveseat';
    return slug(t.replace(/\([^)]*\)/g, ''));
  }

  /* Why a live feed cannot be used, or '' when it can. */
  function feedProblem(live, market) {
    if (!live || typeof live !== 'object') return 'the feed is empty';
    if (live.version !== 1) return 'the feed is not version 1';
    var markets = live.markets || {};
    var names = market ? [market] : ['UK', 'US'];
    for (var i = 0; i < names.length; i++) {
      var block = markets[names[i]];
      if (block && isArray(block.ranges) && block.ranges.length) return '';
    }
    return 'the feed has no ranges for ' + (market || 'either market');
  }

  function findByKey(list, key) {
    for (var i = 0; i < (list || []).length; i++) {
      if (list[i] && list[i].key === key) return list[i];
    }
    return null;
  }

  /* The Worker validates every figure, but the page must not trust the
     network: a size with a number that is not finite and above zero is
     treated as if the feed had rejected it. */
  function cleanSize(s) {
    if (!s || !s.key) return null;
    var w = posNum(s.w), h = posNum(s.h), d = posNum(s.d);
    if (w == null || h == null || d == null) return null;
    var access = null;
    if (s.access != null) {
      access = posNum(s.access);
      if (access == null) return null;
    }
    return {
      key: String(s.key), label: String(s.label == null ? s.key : s.label),
      w: w, h: h, d: d, access: access,
      feet: (isNum(s.feet) && s.feet >= 0) ? s.feet : null
    };
  }

  function mergeRange(bundled, live, rejected) {
    var named = {};
    rejected.forEach(function (row) {
      if (!row || row.size == null) return;
      var r = lower(row.range);
      if (r !== lower(live.name) && slug(row.range) !== live.key) return;
      named[sizeKeyOf(row.size)] = true;
      named['label:' + lower(row.size)] = true;
    });
    function isNamed(size) { return !!(named[size.key] || named['label:' + lower(size.label)]); }

    var sizes = [];
    var seen = {};
    var good = 0;
    (isArray(live.sizes) ? live.sizes : []).forEach(function (s) {
      var key = s && s.key;
      if (!key || seen[key]) return;
      var mine = bundled ? findByKey(bundled.sizes, key) : null;
      var clean = cleanSize(s);
      if (clean) good++;
      if (mine && (!clean || isNamed(mine))) {
        sizes.push(mine);
        seen[key] = true;
      } else if (clean) {
        sizes.push(clean);
        seen[key] = true;
      }
    });
    /* Not one size that passes the guard: the payload is broken, not
       the range. Leave the bundled range whole. */
    if (!good) return null;

    /* A size the feed rejected is missing from its list. A typo in the
       sheet must not remove a size from the tool, so it keeps its
       bundled figures and goes back in ahead of the first size that
       followed it in the bundled order. */
    var order = (bundled && bundled.sizes) || [];
    function placeOf(key) {
      for (var i = 0; i < order.length; i++) if (order[i].key === key) return i;
      return -1;
    }
    order.forEach(function (mine, i) {
      if (seen[mine.key] || !isNamed(mine)) return;
      var at = sizes.length;
      for (var j = 0; j < sizes.length; j++) {
        if (placeOf(sizes[j].key) > i) { at = j; break; }
      }
      sizes.splice(at, 0, mine);
      seen[mine.key] = true;
    });

    function flag(name) {
      if (live[name] === true || live[name] === false) return live[name];
      return bundled ? bundled[name] : null;
    }
    return {
      key: live.key,
      name: live.name || (bundled && bundled.name) || live.key,
      type: live.type || (bundled && bundled.type) || 'sofa',
      /* An older payload has no Frame Design, so these arrive as null
         and the bundled flag stands. */
      split: flag('split'),
      detachable: flag('detachable'),
      frameDesign: isArray(live.frameDesign) ? live.frameDesign : ((bundled && bundled.frameDesign) || null),
      sizes: sizes
    };
  }

  function mergeMarket(market, bundled, live, rejected) {
    if (!live || !isArray(live.ranges) || !live.ranges.length) return bundled;
    /* Centimetres relabelled as inches would be far worse than a stale
       figure, so a block in the wrong unit is ignored whole. */
    var unit = (bundled && bundled.unit) || MARKET_UNIT[market];
    if (unit && live.unit !== unit) return bundled;

    var mine = (bundled && isArray(bundled.ranges)) ? bundled.ranges : [];
    var used = {};
    var ranges = mine.map(function (b) {
      var l = findByKey(live.ranges, b.key);
      if (!l) return b;
      used[b.key] = true;
      /* A range the feed sends with no usable size is treated as absent:
         far more likely a sync hiccup than a discontinued range. */
      return mergeRange(b, l, rejected) || b;
    });
    live.ranges.forEach(function (l) {
      if (!l || !l.key || used[l.key]) return;
      used[l.key] = true;
      var fresh = mergeRange(null, l, rejected);
      if (fresh) ranges.push(fresh);
    });
    return { unit: live.unit, ranges: ranges };
  }

  /* Bundled copy + live feed -> the feed the page uses. A feed that
     cannot be used returns the bundled object itself, untouched. Pass
     the market to insist the feed covers it. */
  function mergeFeed(bundled, live, market) {
    if (feedProblem(live, market)) return bundled;
    var mine = (bundled && bundled.markets) || {};
    var theirs = live.markets || {};
    var rejected = isArray(live.rejected) ? live.rejected : [];
    var out = {
      version: 1,
      generatedAt: live.generatedAt || (bundled && bundled.generatedAt) || null,
      markets: {},
      rejected: rejected
    };
    var names = [];
    var k;
    for (k in mine) if (Object.prototype.hasOwnProperty.call(mine, k)) names.push(k);
    for (k in theirs) if (Object.prototype.hasOwnProperty.call(theirs, k) && !has(names, k)) names.push(k);
    names.forEach(function (name) {
      var forMarket = rejected.filter(function (row) { return row && row.market === name; });
      var merged = mergeMarket(name, mine[name], theirs[name], forMarket);
      if (merged) out.markets[name] = merged;
    });
    return out;
  }

  /* ============ 3b. THE SOFA ============ */
  /* The sofa half of the page state, as plain functions of it:
       { modelKey, sizeKey, dims: { w, h, d, access, feet }, base,
         touched, bad, legsRemovable, split, dimsEdited, flagsEdited }
     base is what a size chip filled in, kept so the page can tell a
     standard sofa from one the customer has typed over. */

  var DIM_KEYS = ['w', 'h', 'd', 'access', 'feet'];
  var DIM_NAMES = { w: 'external width', h: 'external height', d: 'external depth', access: 'access height', feet: 'leg height' };

  function blankDims() { return { w: null, h: null, d: null, access: null, feet: null }; }
  function copyOf(o) {
    var out = {};
    for (var k in (o || {})) if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = o[k];
    return out;
  }
  function round1(v) { return Math.round(v * 10) / 10; }

  /* A range is picked. Everything about the last sofa goes: its size,
     its figures and its flags. The old tool kept the previous sofa's
     numbers under the new range's name. model is null for "Custom". */
  function pickModel(model) {
    return {
      modelKey: model ? model.key : 'custom',
      sizeKey: null,
      dims: blankDims(),
      base: null,
      touched: {},
      bad: {},
      /* Custom starts with both flags unticked, the cautious reading
         until the customer says otherwise. */
      legsRemovable: model ? !!model.legsRemovable : false,
      split: model ? !!model.split : false,
      dimsEdited: false,
      flagsEdited: false
    };
  }

  /* The sheet gives the split frame per range, but an armchair is made
     in one piece whatever its range. Without this the engine "halves"
     an 88 cm chair into two 49 cm pieces and the page offers that as a
     way in. */
  function splitFor(model, sizeKey) {
    return !!(model && model.split) && String(sizeKey || '').indexOf('armchair') !== 0;
  }

  /* A size chip is picked: its standard figures, or for "Bespoke size"
     the range's frame numbers with the width left to be typed. */
  function pickSize(model, sizeKey) {
    var s = sizeKey === 'bespoke' ? null : findByKey(model.sizes, sizeKey);
    var ref = s || model.sizes[Math.floor(model.sizes.length / 2)];
    var base = { w: s ? s.w : null, h: ref.h, d: ref.d, access: ref.access, feet: ref.feet == null ? null : ref.feet };
    return {
      sizeKey: sizeKey, dims: copyOf(base), base: base, touched: {}, bad: {}, dimsEdited: false,
      split: splitFor(model, sizeKey), flagsEdited: false, legsRemovable: !!model.legsRemovable
    };
  }

  /* One figure typed into the editor. Nought or less is never a
     measurement, so it is held as blank and remembered as a slip. */
  function editDim(sofa, key, raw) {
    var v = (typeof raw === 'number') ? raw : parseFloat(raw);
    var blank = raw === '' || raw == null || !isNum(v);
    var dims = copyOf(sofa.dims), touched = copyOf(sofa.touched), bad = copyOf(sofa.bad);
    var base = sofa.base;
    dims[key] = (!blank && v > 0) ? v : null;
    /* No legs at all is a fair answer to "leg height". */
    bad[key] = !blank && !(v > 0) && !(key === 'feet' && v === 0);
    touched[key] = true;
    /* A taller or shorter frame takes the height with the legs off
       with it, until the customer gives that figure themselves. */
    if (key === 'h' && base && base.h != null && base.access != null && !touched.access) {
      dims.access = dims.h != null ? posNum(round1(base.access + dims.h - base.h)) : base.access;
    }
    return { dims: dims, touched: touched, bad: bad, dimsEdited: true };
  }

  /* True when the figures are no longer the standard ones for the
     size that was picked. */
  function isBespoke(sofa) {
    var base = sofa.base, d = sofa.dims || {};
    if (!base) return false;
    for (var i = 0; i < DIM_KEYS.length; i++) {
      var a = d[DIM_KEYS[i]], b = base[DIM_KEYS[i]];
      if ((a == null) !== (b == null) || (a != null && Math.abs(a - b) > 0.05)) return true;
    }
    return false;
  }

  /* The access height the check uses. A figure the customer has typed
     that is taller than the frame is a slip, so the frame height stands
     in for it. Standard figures from the sheet are passed on as they
     are: the engine already takes the taller of the two. */
  function accessUsed(sofa) {
    var d = sofa.dims || {};
    if (d.access != null && d.h != null && d.access > d.h + 0.05 && (!sofa.base || isBespoke(sofa))) return d.h;
    return d.access;
  }

  /* What the editor has to say about the figures as they stand.
     ok is what unlocks steps two and three. */
  function sofaNotes(sofa, unit) {
    sofa = sofa || {};
    var u = unit || UNIT;
    var d = sofa.dims || {};
    var bad = sofa.bad || {};
    var out = { ok: false, errors: [], notes: [], bespoke: isBespoke(sofa) };
    var slips = DIM_KEYS.filter(function (k) { return !!bad[k]; }).map(function (k) { return DIM_NAMES[k]; });
    if (slips.length) {
      out.errors.push('Every measurement needs to be more than nought, so please check the ' + listOf(slips) + '.');
    }
    if (d.access != null && accessUsed(sofa) !== d.access) {
      out.notes.push('The access height cannot be more than the external height, so we have used ' + show(d.h, u) + ' for both.');
    }
    var own = !sofa.base || (sofa.base.w == null) || Math.abs(d.w - sofa.base.w) > 0.05 || Math.abs(d.d - sofa.base.d) > 0.05;
    if (d.w != null && d.d != null && d.w < d.d && own) {
      out.notes.push('The width is smaller than the depth. Is that the right way round? The width is the long side, from arm to arm.');
    }
    var picked = sofa.modelKey === 'custom' || (!!sofa.modelKey && !!sofa.sizeKey);
    out.ok = picked && !slips.length && d.w > 0 && d.d > 0 && d.h > 0;
    return out;
  }

  /* The sofa as the engine wants it. With the legs on, the transit
     height is simply the external height, so there is no leg height to
     add: feet is only there to feed fixed legs through a doorway. */
  function engineSofaOf(sofa) {
    var d = (sofa && sofa.dims) || {};
    return {
      L: d.w, D: d.d, H: d.h, access: accessUsed(sofa || {}), feet: d.feet == null ? null : d.feet,
      legsOff: !!(sofa && sofa.legsRemovable), splitAvailable: !!(sofa && sofa.split)
    };
  }

  /* What the answer and the email say about the sofa that was checked. */
  function infoOf(sofa, model, market, unit, result) {
    var u = unit || UNIT;
    var d = (sofa && sofa.dims) || {};
    var s = model ? findByKey(model.sizes, sofa.sizeKey) : null;
    var bits = [];
    if (d.w != null) bits.push(show(d.w, u) + ' wide');
    if (d.d != null) bits.push(show(d.d, u) + ' deep');
    if (d.h != null) bits.push(show(d.h, u) + ' high');
    var A = result && result.sofa && result.sofa.A;
    var legs = 'with the legs on';
    if (sofa.legsRemovable) {
      legs = 'with the legs off' + ((isNum(A) && d.h != null && A < d.h - 0.05) ? ' (' + show(A, u) + ' high)' : '');
    }
    return {
      name: model ? model.name + (s ? ' ' + s.label : ', bespoke size') : 'Custom sofa',
      model: model ? model.name : '', market: market, dims: listOf(bits), legs: legs,
      legsOff: !!sofa.legsRemovable, splitAvailable: !!sofa.split, detachable: !!(model && model.detachable),
      bespoke: isBespoke(sofa),
      /* An armchair in a range that is otherwise made split. */
      onePiece: !!(model && model.split && !splitFor(model, sofa.sizeKey)),
      /* An armchair is not "the sofa" in the sentences about it. */
      piece: String(sofa.sizeKey || '').indexOf('armchair') === 0 ? 'armchair' : 'sofa'
    };
  }

  /* ============ 4. THE ROUTE ============ */

  /* Form values -> the engine's checks. The values object holds the
     numbers as typed, plus inputUnit (what they were typed in) and unit
     (the market's, which the engine works in). A blank is null and stays
     null: blank means "not measured", never "fine". Nought or less is a
     slip of the finger and is read as blank too: the one way to say
     "nothing to go through" is the explicit roomOpen. The ids are fixed.

     A stop the customer has declared is never dropped for want of a
     figure. A corner that is ticked, a lift, stairs, or a door in a side
     wall is always asked, and comes back as "not checked yet". */
  function buildChecks(values) {
    var v = values || {};
    var unit = v.unit || UNIT;
    var from = v.inputUnit || unit;
    function n(key) {
      var x = v[key];
      if (typeof x === 'string' && x !== '') x = parseFloat(x);
      if (!isNum(x) || x <= 0) return null;
      return convert(x, from, unit);
    }
    function least(a, b) {
      if (a == null) return b;
      return (b == null || a < b) ? a : b;
    }

    var stairs = !!v.stairs;
    /* A front door that opens into a room leaves no hallway on the form. */
    var noHall = v.frontEntry === 'room';
    var frontW = n('frontW'), frontH = n('frontH');
    var hallW = noHall ? null : n('hallW'), hallH = noHall ? null : n('hallH');
    var hallLen = noHall ? null : n('hallLen'), hallPinch = noHall ? null : n('hallPinch');
    var upW = n('upW'), upH = n('upH');
    var roomW = n('roomW'), roomH = n('roomH');
    var checks = [];

    if (v.outsideSteps) {
      checks.push({ id: 'outside', stop: 'outside', kind: 'flag', code: 'outside-tight', tier: 'tight' });
    }
    /* thin marks a doorway: the only place fixed legs can be fed
       through one pair at a time. */
    if (frontW != null) {
      checks.push({ id: 'front', stop: 'front', kind: 'passage', w: frontW, h: frontH, thin: true });
    }
    /* A flat's front door is usually in the side wall of a shared
       corridor: the same turn as a room door off a hall, on the outside.
       No ceiling given is read as not measured, the cautious way.
       door marks a turn through a doorway, so that a door height left
       blank is judged under an ordinary door and not as no door at all. */
    if (v.frontOutside === 'side') {
      checks.push({
        id: 'front-outside', stop: 'front', kind: 'turn', a: n('outsideW'), b: frontW, door: true, doorH: frontH,
        ceilA: n('outsideH'), ceilB: hallH, lenA: null, lenB: null, pitched: false, onEndOk: true
      });
    }
    if (v.frontEntry === 'side') {
      checks.push({
        id: 'front-turn', stop: 'front', kind: 'turn', a: hallW, b: frontW, door: true, doorH: frontH,
        ceilA: hallH, ceilB: null, lenA: hallLen, lenB: null, pitched: false, onEndOk: true
      });
    }
    /* A turn on each side of one door is a dog-leg. The engine checks
       each turn as if the other side were open, so the pair is held at
       "let us check" whatever the two say. */
    if (v.frontOutside === 'side' && v.frontEntry === 'side') {
      checks.push({ id: 'front-both', stop: 'front', kind: 'flag', code: 'double-turn', tier: 'tight' });
    }
    /* Along the hall the narrowest pinch decides. The turns below use
       the wall to wall width: a radiator metres from a corner does not
       narrow the corner. */
    if (!noHall && (hallW != null || hallPinch != null)) {
      checks.push({ id: 'hall', stop: 'hall', kind: 'passage', w: least(hallW, hallPinch), h: hallH, thin: false });
    }
    if (!noHall && v.corner) {
      checks.push({
        id: 'hall-corner', stop: 'hall', kind: 'turn', a: hallW, b: n('hallW2'), doorH: null,
        ceilA: hallH, ceilB: hallH, lenA: hallLen, lenB: null, pitched: false, onEndOk: true
      });
    }
    if (v.lift) {
      checks.push({
        id: 'lift', stop: 'lift', kind: 'lift', thin: true,
        doorW: n('liftDoorW'), doorH: n('liftDoorH'), carW: n('liftW'), carD: n('liftD'), carH: n('liftH')
      });
    }
    if (stairs) {
      checks.push({
        id: 'stairs', stop: 'stairs', kind: 'stairs', shape: v.stairShape || null,
        w: n('stairW'), headroom: n('stairH'),
        foot: v.stairFoot === 'turn' ? { a: hallW, ceil: hallH } : null,
        top: v.stairTop === 'turn' ? { depth: n('topD'), ceil: n('topC') } : null,
        landing: { depth: n('landingD'), width: n('landingW'), ceil: n('landingC') }, w2: null,
        open: v.stairSide === 'banister'
      });
    }
    if (stairs && upW != null) {
      checks.push({ id: 'landing', stop: 'landing', kind: 'passage', w: upW, h: upH, thin: false });
    }
    if (v.roomOpen) {
      /* "No door to go through", chosen outright. This is the only
         thing that reaches the engine as a width of nought. */
      checks.push({ id: 'room', stop: 'room', kind: 'passage', w: 0, h: null, thin: true });
    } else {
      if (roomW != null) {
        checks.push({ id: 'room', stop: 'room', kind: 'passage', w: roomW, h: roomH, thin: true });
      }
      if (v.roomEntry === 'side') {
        checks.push({
          id: 'room-turn', stop: 'room', kind: 'turn',
          a: stairs ? upW : hallW, b: roomW, door: true, doorH: roomH,
          ceilA: stairs ? upH : hallH, ceilB: n('roomC'),
          lenA: stairs ? null : hallLen, lenB: null, pitched: false, onEndOk: true
        });
      }
    }
    return checks;
  }

  function typed(v, key) { return isNum(v[key]) && v[key] > 0; }

  /* What must be answered before the engine can be asked. Only the
     choices that decide whether the sofa has to turn: a stop with a
     width typed in needs its choice. Everything else may stay blank. */
  function validateRoute(values) {
    var v = values || {};
    var out = [];
    if (typed(v, 'frontW') && !v.frontEntry) {
      out.push({ key: 'frontEntry', text: 'Front door: tell us what is behind the door, so we know whether the sofa has to turn.' });
    }
    if (v.stairs && typed(v, 'stairW')) {
      if (!v.stairShape) out.push({ key: 'stairShape', text: 'Stairs: choose the shape of the staircase.' });
      if (v.stairShape !== 'spiral') {
        if (!v.stairFoot) out.push({ key: 'stairFoot', text: 'Stairs: tell us how you reach the bottom step.' });
        if (!v.stairTop) out.push({ key: 'stairTop', text: 'Stairs: tell us what happens at the top.' });
      }
    }
    if (typed(v, 'roomW') && !v.roomEntry) {
      out.push({ key: 'roomEntry', text: 'Room door: tell us whether the door is straight ahead or in a side wall.' });
    }
    return out;
  }

  /* Believable range for each kind of figure, in centimetres. Outside
     it the likeliest cause is the wrong unit: 198 typed with inches
     selected is a door five metres tall, and it would sail through as a
     fit. So the customer is asked once to look again, and may carry on
     if the number is right. */
  var SANE = { doorW: [40, 250], doorH: [120, 320], span: [40, 600], ceil: [150, 600], len: [50, 2500] };
  var FIELDS = {
    frontW: ['Front door width', 'doorW'], frontH: ['Front door height', 'doorH'],
    outsideW: ['Width outside the front door', 'span'], outsideH: ['Ceiling outside the front door', 'ceil'],
    hallW: ['Hallway width', 'span'], hallPinch: ['Hallway pinch point', 'span'], hallH: ['Hallway ceiling', 'ceil'],
    hallLen: ['Hallway length', 'len'], hallW2: ['Hallway width after the turn', 'span'],
    liftDoorW: ['Lift door width', 'doorW'], liftDoorH: ['Lift door height', 'doorH'],
    liftW: ['Lift width', 'span'], liftD: ['Lift depth', 'span'], liftH: ['Lift height', 'ceil'],
    stairW: ['Stair width', 'span'], stairH: ['Stair headroom', 'ceil'],
    landingD: ['Landing depth', 'span'], landingW: ['Landing width', 'span'], landingC: ['Landing ceiling', 'ceil'],
    topD: ['Depth at the top of the stairs', 'span'], topC: ['Ceiling at the top of the stairs', 'ceil'],
    upW: ['Upstairs width', 'span'], upH: ['Upstairs ceiling', 'ceil'],
    roomW: ['Room door width', 'doorW'], roomH: ['Room door height', 'doorH'], roomC: ['Room ceiling', 'ceil']
  };

  function oddValues(values) {
    var v = values || {};
    var unit = v.inputUnit || v.unit || UNIT;
    var out = [];
    NUM_KEYS.forEach(function (key) {
      if (isNum(v[key]) && v[key] <= 0) {
        /* The old tool read a typed nought as "open plan" and waved
           the sofa through. Here it is a blank, and the customer is
           told so. */
        out.push({
          key: key,
          text: FIELDS[key][0] + ': ' + plain(v[key]) + ' is not a measurement we can use, so we have left it out.' +
            (key === 'roomW' ? ' If there is no door at all, choose "No door to go through" from the list.' : '')
        });
        return;
      }
      if (!typed(v, key)) return;
      var cm = convert(v[key], unit, 'cm');
      var band = SANE[FIELDS[key][1]];
      if (cm >= band[0] && cm <= band[1]) return;
      out.push({
        key: key,
        text: FIELDS[key][0] + ': ' + show(v[key], unit) + ' looks ' + (cm < band[0] ? 'very small' : 'very large') + '.'
      });
    });
    return out;
  }

  /* ============ 5. THE WORDS ============ */

  /* [ standalone label, label under its stop heading ] */
  var LABELS = {
    'outside':         ['Outside the front door', 'The approach'],
    'front':           ['Front door', 'Through the doorway'],
    'front-outside':   ['Turning in through the front door', 'The turn in from outside'],
    'front-turn':      ['Turning in from the front door', 'The turn inside the door'],
    'front-both':      ['Both sides of the front door', 'The two turns together'],
    'hall':            ['Hallway', 'Along the hallway'],
    'hall-corner':     ['Hallway corner', 'The corner'],
    'lift':            ['Lift', 'The lift'],
    'lift:door':       ['Lift door', 'The lift door'],
    'lift:car':        ['Inside the lift', 'Inside the lift'],
    'stairs':          ['Stairs', 'The stairs'],
    'stairs:flight':   ['Stair width', 'The width of the flight'],
    'stairs:headroom': ['Stair headroom', 'Headroom over the steps'],
    'stairs:foot':     ['Turning onto the stairs', 'The turn onto the stairs'],
    'stairs:landing':  ['Stair landing', 'The turn on the landing'],
    'stairs:top':      ['Top of the stairs', 'The turn at the top'],
    'landing':         ['Upstairs landing', 'Along the landing'],
    'room':            ['Room door', 'Through the doorway'],
    'room-turn':       ['Turning through the room door', 'The turn into the room'],
    'winders':         ['Winding steps', 'The winding steps'],
    'other':           ['This point', 'This point']
  };

  var STOP_TITLES = {
    outside: 'Outside', front: 'Front door', hall: 'Hallway', lift: 'Lift',
    stairs: 'Stairs', landing: 'Upstairs', room: 'Room door'
  };

  /* What to measure when a check could not be run. */
  var MEASURE = {
    'front': 'Measure the clear width and height of the front door, with the door wide open.',
    'front-outside': 'Add the front door width, and the clear width of the corridor or passage outside it, and we can check the turn in.',
    'front-turn': 'Add the front door width and the hallway width, and we can check the turn inside the door.',
    'hall': 'Measure the clear width of the hallway, wall to wall.',
    'hall-corner': 'Add the hallway width before and after the corner.',
    'lift': 'Measure the lift door, and the inside of the lift: width, depth and height.',
    'lift:door': 'Measure the clear opening of the lift door, width and height.',
    'lift:car': 'Measure inside the lift: width, depth and height.',
    'stairs': 'Measure the clear width of the stairs at the narrowest point, from the wall to the handrail.',
    'stairs:flight': 'Measure the clear width of the stairs at the narrowest point, from the wall to the handrail.',
    'stairs:headroom': 'Measure straight up from the edge of a step to the lowest point above it.',
    'stairs:foot': 'Add the width of the hallway at the foot of the stairs.',
    'stairs:landing': 'Measure the landing: its depth, and its width along the long side.',
    'stairs:top': 'Measure the clear depth at the top, from the top step to the wall or banister facing you.',
    'landing': 'Measure the width of the landing or hall upstairs.',
    'room': 'Measure the clear width and height of the room door, with the door wide open.',
    'room-turn': 'Add the room door width, and the width of the hallway or landing you turn from.',
    'other': 'Measure the clear width and height here and we can check it.'
  };

  var METHOD_WORDS = {
    straight: 'carried straight through',
    flat: 'carried flat',
    onend: 'stood on its end',
    lean: 'stood on its end and leaned through, base first',
    tilt: 'tipped part way up',
    thread: 'with its legs fed through one pair at a time'
  };

  var FLAG_COPY = {
    'stair-turn-cap': 'We always look at turning stairs ourselves, because newel posts, handrails and step edges do not show up on a tape measure.',
    'winders': 'Winding steps leave no flat floor to rest anything on, so the team always checks these.',
    'spiral': 'Spiral stairs are always one for the team, so send us a short video and we will take a proper look.',
    'open-banister': 'With an open banister on one side, the team can sometimes lift it over the handrail, so do tell us about it.',
    'very-tight': 'It is very close, so the team will want to check before anything is made.',
    'too-close': 'It is too close to call, because every frame varies a little, so this is one for the team.',
    'legs-thread': 'The legs have to be fed through the doorway one pair at a time, which the team will want to check.',
    'outside-tight': 'Steps or a tight path right outside can make the approach awkward, so we like to see it first.',
    'double-turn': 'The sofa has to turn on both sides of this door, so the team will want to look at the two turns together.',
    'too-narrow': 'One side is narrower than it is at its slimmest, so it needs a proper look from the team.',
    'assumed': 'We have assumed a turn here, to be on the safe side.'
  };
  var FLAG_ORDER = ['needs-ceiling', 'very-tight', 'too-close', 'legs-thread', 'stair-turn-cap', 'winders', 'open-banister',
    'spiral', 'outside-tight', 'double-turn', 'too-narrow', 'assumed'];

  /* Where the sofa is stood up at each turn, for "needs a ceiling of". */
  var STAND = {
    'front-outside': 'outside the door', 'front-turn': 'in the hallway', 'hall-corner': 'in the hallway',
    'room-turn': 'in the hallway', 'stairs:foot': 'at the foot of the stairs',
    'stairs:landing': 'over the landing', 'stairs:top': 'at the top of the stairs'
  };

  /* The route figures each line rests on. A line that rests on a figure
     filled in by a "closest match" preset says so. Presets exist for
     the front door, the hallway, the stairs and the room door only. */
  var LINE_KEYS = {
    'front': ['frontW', 'frontH'],
    'front-outside': ['frontW', 'frontH', 'hallH'],
    'front-turn': ['frontW', 'frontH', 'hallW', 'hallH'],
    'hall': ['hallW', 'hallH'],
    'hall-corner': ['hallW', 'hallH'],
    'stairs:flight': ['stairW'],
    'stairs:headroom': ['stairW', 'stairH'],
    'stairs:foot': ['stairW', 'hallW', 'hallH'],
    'stairs:landing': ['stairW'],
    'stairs:top': ['stairW'],
    'room': ['roomW', 'roomH'],
    'room-turn': ['roomW', 'roomH', 'hallW', 'hallH']
  };
  var TYPICAL_TAG = ' (typical size)';

  function spareBit(spare, u) {
    if (!isNum(spare)) return '';
    if (spare >= spareCap(u) - 0.05) return ' with more than ' + show(spareCap(u), u) + ' to spare';
    if (spare < 0.1) return ' with nothing to spare';
    return ' with ' + showDown(spare, u) + ' to spare';
  }

  function tightBit(spare, u, M) {
    if (!isNum(spare)) return ', but it is inside our ' + show(M, u) + ' safety margin';
    if (spare < 0.1) return ', but with nothing to spare';
    return ', but with only ' + showDown(spare, u) + ' to spare, which is inside our ' + show(M, u) + ' safety margin';
  }

  /* A carry that works on paper with less than the engine's floor to
     spare. It is a "talk to us", but an honest one: by the numbers it
     goes, and the customer should hear that. */
  function closeText(how, u) {
    return 'too close to call. By the numbers it ' + how + ', but with under ' + showExact(floorOf(u), u) +
      ' of clearance, and every frame varies a little. One for the team.';
  }

  /* How the sofa goes through an opening, from the engine's choice of
     what lies across it. "On its side" is the long way through with the
     height across the gap, which is how a sofa passes most doors. */
  function carryWords(d, sofa) {
    if (d.carry === 'forwards') return ' facing forwards';
    if (d.carry !== 'side') return '';
    if (isNum(d.across) && isNum(d.up) && isNum(sofa.D) && Math.abs(d.across - d.up) > 1e-6 &&
        Math.abs(d.across - sofa.D) < 1e-6) return ' the right way up';
    return ' on its side';
  }

  /* One engine result -> { label, short, text }. Total on purpose: any
     mix of kind, method, tier and flags gives a sentence, so a result
     shape this file has not met still reads sensibly. */
  function describe(r, ctx) {
    r = r || {};
    ctx = ctx || {};
    var u = ctx.unit || UNIT;
    var M = isNum(ctx.margin) ? ctx.margin : marginOf(u);
    var d = r.data || {};
    var sofa = ctx.sofa || {};
    var flags = r.flags || [];
    var id = String(r.id || r.stop || 'other');
    var part = id.indexOf(':') === -1 ? '' : id.split(':')[1];
    var names = LABELS[id] || LABELS[r.stop] || LABELS.other;
    if (part === 'landing' && (has(flags, 'winders') || ctx.shape === 'winder')) names = LABELS.winders;
    /* A spiral, or a shape the engine does not know, is reported once
       for the whole staircase, whichever part carries the flag. */
    if (has(flags, 'spiral') || has(flags, 'unknown-shape')) names = LABELS.stairs;

    var slim = isNum(d.slim) ? d.slim : sofa.p;
    var len = isNum(d.L) ? d.L : sofa.L;
    var short = isNum(r.shortBy) ? r.shortBy : d.shortBy;
    var pitched = part === 'foot' || part === 'landing' || part === 'top';
    var isTurn = r.kind === 'turn' || pitched || r.method === 'lean' || r.method === 'tilt';
    var typical = !!(ctx.typical && ctx.typical[id]);
    var piece = ctx.piece || 'sofa';
    var close = has(flags, 'too-close');
    var place = (id === 'room-turn' && ctx.upstairs) ? 'on the landing' : (STAND[id] || 'at this spot');
    var said = {};
    var text;

    /* The ceiling a sofa needs to be stood on its end. Outside the
       front door there may be no ceiling at all, which the form cannot
       tell from one nobody measured. */
    function ceilingBit(team) {
      var open = id === 'front-outside';
      said['needs-ceiling'] = true;
      if (!isNum(d.needCeiling)) return ' That depends on the ceiling height ' + place + ', so please measure it.';
      var need = showUp(d.needCeiling, u);
      if (team) {
        return ' Stood on its end it would need ' + need + ' of clear height ' + place + ', which is more than most ceilings give.' +
          (open ? ' If the passage is open to the sky, or the ceiling really is that high, tell the team, because that may be all it needs.'
                : ' If the ceiling there really is that high, measure it and tell the team, because that may be all it needs.');
      }
      return open
        ? ' That needs ' + need + ' of clear height ' + place + '. If the passage is open to the sky there is nothing to measure. If it has a ceiling, please measure it.'
        : ' That needs a clear ceiling of at least ' + need + ' ' + place + ', so please measure it.';
    }

    if (r.kind === 'flag') {
      var code = flags[0] || r.code;
      said[code] = true;
      if (code === 'outside-tight') {
        text = 'steps or a tight path right outside the door can make the approach awkward. Send us a photo or a short video and the team will check it.';
      } else if (code === 'double-turn') {
        text = 'the ' + piece + ' has to turn on both sides of this door, one turn straight after the other. We check each turn on its own, so the team will want to look at the two together.';
      } else {
        text = 'something here needs a look from the team, so do mention it when you get in touch.';
      }

    } else if (has(flags, 'spiral')) {
      said.spiral = true;
      text = 'spiral stairs are always one for the team. Send us a short video and we will take a proper look.';

    } else if (has(flags, 'unknown-shape')) {
      text = 'we could not tell what shape these stairs are, so this is one for the team. Send us a short video and we will take a proper look.';

    } else if (r.tier === 'unknown') {
      said['missing-width'] = said['headroom-not-measured'] = said['landing-not-measured'] = said['lift-not-measured'] = true;
      text = 'not checked yet. ';
      if (part === 'headroom' && isNum(d.need)) {
        text += 'Carried up the flight, the ' + piece + ' needs about ' + showUp(d.need, u) + ' of clear height above the steps. ';
      }
      if (ctx.noHall && (id === 'stairs:foot' || (id === 'room-turn' && !ctx.upstairs))) {
        /* The front door opens straight into a room, so the form has
           no hallway to fill in. Do not send the customer looking for
           a field that is not there. */
        text += 'This turn starts in the room your front door opens into, and we have no width for that. Tell the team how much clear floor there is and they will check it.';
      } else if (id === 'room-turn' && isNum(d.b) && !isNum(d.a)) {
        text += 'Add the width of the hallway or landing you turn from, and we can check this turn.';
      } else if (id === 'front-turn' && isNum(d.b) && !isNum(d.a)) {
        text += 'Add the hallway width, and we can check the turn inside the door.';
      } else if (id === 'front-outside' && isNum(d.b) && !isNum(d.a)) {
        text += 'Add the clear width of the corridor or passage outside, and we can check the turn in through the door.';
      } else if (id === 'hall-corner' && isNum(d.a) && !isNum(d.b)) {
        text += 'Add the width of the hallway after the turn, and we can check the corner.';
      } else if (part === 'car' && isNum(d.carW) && isNum(d.carD) && isNum(d.carH)) {
        /* The car is measured: it is the door that is missing. */
        text += 'It is too long to go in flat, so add the width and height of the lift door and we can check whether it stands on its end inside.';
      } else {
        text += MEASURE[id] || MEASURE[r.stop] || MEASURE.other;
      }

    } else if (part === 'headroom') {
      text = isNum(d.need)
        ? 'carried up the flight, the ' + piece + ' needs about ' + showUp(d.need, u) + ' of clear height' +
          (isNum(d.headroom) ? (typical ? ', and typical stairs like these give ' : ' and you have ') + show(d.headroom, u) : '')
        : 'the ' + piece + ' is carried up the flight at an angle, which takes more headroom than it looks';
      if (close) {
        said['too-close'] = true;
        text += '. That is too close to call: there is under ' + showExact(floorOf(u), u) + ' of clearance, and every frame varies a little. One for the team.';
      } else if (r.tier === 'fit') text += ', so it clears' + spareBit(r.spare, u) + '.';
      else if (r.tier === 'tight') text += '. It clears' + tightBit(r.spare, u, M) + '.';
      else text += (isNum(short) ? ', so it is about ' + showUp(short, u) + ' short' : '') + '. One for the team.';

    } else if (part === 'car') {
      if (close) {
        said['too-close'] = true;
        text = closeText('goes in flat', u);
      } else if (r.method === 'flat' && r.tier === 'fit') text = 'goes in flat' + spareBit(r.spare, u) + '.';
      else if (r.method === 'flat') text = 'goes in flat' + tightBit(r.spare, u, M) + '.';
      else if (r.tier === 'team') text = 'tighter than our usual carries allow, flat or stood on its end. One for the team.';
      else text = 'too long to go in flat, but it should stand on its end inside.';

    } else if (isTurn) {
      var door = id === 'front-outside' || id === 'front-turn' || id === 'room-turn';
      var round = door ? 'through the doorway' : (id === 'hall-corner' ? 'round the corner' : 'here');
      /* The flat limit is only worth quoting on a level turn that the
         sofa is too long for. On stairs the limit is compared with the
         sofa's length along the pitch, which is not a number a customer
         can check against anything. Nor does it mean much for a chair
         that is deeper than it is wide. */
      var limit = (!pitched && isNum(d.flatLimit) && d.flatLimit > 0 && isNum(len) && d.flatLimit < len &&
          !(isNum(d.fat) && len < d.fat))
        ? ' The longest sofa that turns flat here is about ' + showDown(d.flatLimit, u) + ', and this one is ' + show(len, u) + '.'
        : '';
      /* "Too long" only where the limit bears it out. A flat turn that
         was merely too close to call is "too tight". */
      var tooLong = (limit ? 'too long' : 'too tight') + ' to turn ' + round + ' lying flat';
      if (has(flags, 'too-narrow')) {
        said['too-narrow'] = true;
        if (isNum(slim) && isNum(len) && len < slim) slim = len;
        text = 'one side of this turn is narrower than the ' + piece + ' at its slimmest' +
          (isNum(slim) ? ' (' + show(slim, u) + ')' : '') + ', so it needs a proper look from the team.';
      } else if (close) {
        said['too-close'] = true;
        text = closeText('turns flat', u);
      } else if (r.method === 'flat' && r.tier === 'fit') {
        text = 'turns flat' + spareBit(r.spare, u) + '.';
      } else if (r.method === 'flat' && r.tier === 'tight' && (has(flags, 'stair-turn-cap') || has(flags, 'winders'))) {
        text = 'by the numbers it turns flat' + spareBit(r.spare, u) + '.';
      } else if (r.method === 'flat' && r.tier === 'tight') {
        text = 'turns flat' + tightBit(r.spare, u, M) + '.';
      } else if (r.method === 'onend') {
        /* The ordinary way a sofa gets through a door off a hallway,
           so it is said plainly. The headline says how ordinary, once,
           and still asks for a check. */
        text = tooLong + ', but it should go ' + (door ? 'in' : 'round') + ' stood on its end.' + limit;
      } else if (r.method === 'lean') {
        text = tooLong + ', and taller than the doorway when stood on its end, but it should lean through: stood up first, then tipped through the doorway base first, with the top coming down as it goes.';
      } else if (r.method === 'tilt') {
        text = tooLong + ', but it should go round tipped part way up on one end.' + limit;
      } else if (r.tier === 'fit' || r.tier === 'tight') {
        text = 'turns' + (r.tier === 'fit' ? spareBit(r.spare, u) : tightBit(r.spare, u, M)) + '.';
      } else if (has(flags, 'needs-ceiling')) {
        /* Team, but only because it would take a ceiling higher than
           a normal one, and nobody has measured this one. */
        text = tooLong + '.' + limit + ceilingBit(true) + ' Otherwise it is one for the team.';
      } else {
        /* The limit already says how far off the flat carry is. Its
           shortfall on top (every wall moved out by so much) would be a
           second figure for the same thing, and a hopeless-looking one
           where the real miss is a few centimetres of ceiling. */
        text = (pitched || !limit)
          ? 'tighter than our usual carries allow.' + (isNum(short) ? ' It would need about ' + showUp(short, u) + ' more room.' : '')
          : tooLong + ', and by our sums there is no room to stand it on its end and bring it ' + (door ? 'through' : 'round') + ' either.' + limit;
        text += ' One for the team.';
      }

    } else {
      /* A straight run: a doorway, a hallway, a landing or a flight. */
      var along = id === 'hall' || id === 'landing';
      var go = part === 'flight' ? 'goes up the flight' : ((along ? 'goes straight along' : 'goes straight through') + carryWords(d, sofa));
      var isDoor = id === 'front' || id === 'room' || part === 'door';
      var widthOnly = (isDoor && isNum(d.w) && d.h == null) ? ' We have only checked the width, as no height was given.' : '';
      if (r.method === 'open' || d.w === 0) {
        text = 'no door to go through, so there is nothing to check here.';
      } else if (r.method === 'thread' || (r.tier === 'onend' && has(flags, 'legs-thread'))) {
        said['legs-thread'] = true;
        text = 'too tight carried whole, because the legs stay on. The body of the ' + piece + ' clears' + spareBit(d.spare, u) +
          ', and the legs are then fed through the doorway one pair at a time, which the team will want to check.';
      } else if (close) {
        said['too-close'] = true;
        text = closeText(go, u) + widthOnly;
      } else if (r.tier === 'fit') {
        text = go + spareBit(r.spare, u) + '.' + widthOnly;
      } else if (r.tier === 'tight') {
        text = go + tightBit(r.spare, u, M) + '.' + widthOnly;
      } else if (r.tier === 'onend') {
        text = 'too tight carried the ordinary way, but it should go ' + (METHOD_WORDS[r.method] || 'with careful handling') + '.';
      } else {
        text = 'tighter than our usual carries allow' + (isNum(short) ? ', by about ' + showUp(short, u) : '') + '.' +
          (isNum(slim) ? ' The ' + piece + ' is ' + show(slim, u) + ' across its slimmest side.' : '') + ' One for the team.';
      }
    }

    /* In a whole answer (ctx.once is summarise's), a sentence about
       the staircase as a whole is said on the first line that carries
       it and not again on the next two. */
    var once = ctx.once;
    FLAG_ORDER.forEach(function (flag) {
      if (!has(flags, flag) || said[flag]) return;
      if (flag === 'needs-ceiling') { text += ceilingBit(r.tier === 'team'); return; }
      if (once && once[flag]) {
        if (flag === 'very-tight') text += ' This one is very close too.';
        return;
      }
      if (once) once[flag] = true;
      text += ' ' + FLAG_COPY[flag];
    });

    /* Stood on its end, the sofa has to pass under the door it turns
       through. With the door height left blank the engine judges it
       under an ordinary door and flags the result, so the line owns up
       to it and the headline is held back. Where that leaves it with
       the team, say what height would change the answer. */
    var needsDoor = has(flags, 'needs-door-height') ||
      ((r.method === 'onend' || r.method === 'tilt') && !!(ctx.noDoorH && ctx.noDoorH[id]));
    if (needsDoor && r.tier === 'team') {
      text += ' We were not given the height of this doorway' +
        (isNum(d.needDoorHeight) ? ', and it would need to be at least ' + showUp(d.needDoorHeight, u) + ' for the ' + piece + ' to pass under it on its end' : '') +
        ', so please add it.';
    } else if (needsDoor) {
      text += ' We were not given the height of this doorway, which it has to pass under on the way through, so please add ' +
        (said['needs-ceiling'] ? 'that too.' : 'it.');
    }

    var label = names[0], short = names[1];
    if (id === 'hall' && ctx.pinch) {
      label = 'Hallway, at the pinch point';
      short = 'Past the pinch point';
    }
    return { label: label, short: short, text: text, typical: typical, needsDoor: needsDoor };
  }

  /* The sentence for one engine result, standing on its own. */
  function lineFor(result, ctx) {
    var d = describe(result, ctx);
    return d.label + (d.typical ? TYPICAL_TAG : '') + ': ' + d.text;
  }

  /* The same result in a few words, for the email. */
  function shortFor(r, ctx) {
    var u = (ctx && ctx.unit) || UNIT;
    var d = r.data || {};
    var close = has(r.flags, 'too-close');
    var words = describe(r, ctx);
    var bits;
    if (r.kind === 'flag') return words.label + ': to be looked at';
    if (r.tier === 'unknown') bits = ['not checked'];
    else if (r.tier === 'fit') bits = ['fits'];
    else if (r.tier === 'tight') bits = ['close'];
    else if (r.tier === 'onend') {
      bits = [r.method === 'lean' ? 'leaned through' : (r.method === 'tilt' ? 'tipped up' : (r.method === 'thread' ? 'body fits, legs fed through' : 'on its end'))];
    } else bits = [close ? 'too close to call' : 'talk to us'];
    if ((r.tier === 'fit' || r.tier === 'tight' || close) && isNum(r.spare) && r.spare >= 0) {
      bits.push(r.spare >= spareCap(u) - 0.05 ? 'over ' + plain(spareCap(u)) + ' ' + u + ' spare' : down(r.spare) + ' ' + u + ' spare');
    }
    var short = isNum(r.shortBy) ? r.shortBy : d.shortBy;
    if (r.tier === 'team' && !close && isNum(short)) bits.push('about ' + up(short) + ' ' + u + ' short');
    if (has(r.flags, 'needs-ceiling') && isNum(d.needCeiling)) bits.push('needs ' + up(d.needCeiling) + ' ' + u + ' of ceiling');
    if (has(r.flags, 'very-tight')) bits.push('very close');
    if (has(r.flags, 'stair-turn-cap') || has(r.flags, 'winders')) bits.push('turning stairs');
    if (has(r.flags, 'spiral')) bits.push('spiral stairs');
    if (has(r.flags, 'open-banister')) bits.push('open banister');
    if (words.needsDoor) bits.push('door height not given');
    return words.label + ': ' + bits.join(', ');
  }

  function listOf(items) {
    if (items.length < 2) return items.join('');
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }
  function capital(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  function worse(a, b) { return (TIER_RANK[a] || 0) >= (TIER_RANK[b] || 0) ? a : b; }

  /* The one-line recap the answer opens with, and the email repeats. */
  function recapOf(info) {
    var legs = info.legs || (info.legsOff ? 'with the legs off' : 'with the legs on');
    var name = info.name || 'Custom sofa';
    /* A standard size the customer has typed over is no longer that
       size, and the recap must not name it as if it were. */
    if (info.bespoke && !/bespoke/i.test(name)) name += ' with bespoke dimensions';
    return name + (info.dims ? ', ' + info.dims + ', ' + legs : '');
  }

  /* Engine result + form values -> everything the answer panel and the
     email need, as plain data. info describes the sofa that was checked
     (see infoOf). values.preset marks the figures a "closest match"
     filled in and the customer has not typed over. */
  function summarise(result, values, info) {
    values = values || {};
    info = info || {};
    var u = values.unit || UNIT;
    var M = marginOf(u);
    var m = show(M, u);
    var results = ((result && result.results) || []).filter(function (r) { return !!r; });
    if (!results.length) return { empty: true };

    /* Presets are not measurements. Work out which stops, and which
       lines, are standing on a typical size. */
    var preset = values.preset || {};
    function fromPreset(keys) {
      return (keys || []).some(function (k) { return !!preset[k] && typed(values, k); });
    }
    var typicalStops = [];
    [['front', 'the front door'], ['hall', 'the hallway'], ['stair', 'the staircase'], ['room', 'the room door']].forEach(function (p) {
      if (p[0] === 'stair' && !values.stairs) return;
      if (fromPreset(PRESET_KEYS[p[0]])) typicalStops.push(p[1]);
    });
    var typical = {};
    results.forEach(function (r) {
      /* Upstairs, the room door is turned into from the landing. */
      var keys = (r.id === 'room-turn' && values.stairs) ? LINE_KEYS.room : LINE_KEYS[r.id];
      if (fromPreset(keys)) typical[r.id] = true;
    });
    var soft = typicalStops.length > 0;

    var ctx = {
      unit: u, margin: M, sofa: (result && result.sofa) || {}, shape: values.stairShape || null,
      /* True when a turn has no hallway to be measured from. Upstairs,
         the room door is turned into from the landing instead. */
      noHall: values.frontEntry === 'room', upstairs: !!values.stairs, typical: typical, piece: info.piece || 'sofa',
      once: {},
      /* The hallway line is about its narrowest point when one is given. */
      pinch: typed(values, 'hallPinch') && (!typed(values, 'hallW') || values.hallPinch < values.hallW),
      /* Door turns whose door height was left blank. */
      noDoorH: { 'front-turn': !typed(values, 'frontH'), 'front-outside': !typed(values, 'frontH'), 'room-turn': !typed(values, 'roomH') }
    };
    var piece = ctx.piece;
    var spiral = !!values.stairs && values.stairShape === 'spiral';
    var overall = (result.overall && TIER_RANK[result.overall] != null) ? result.overall : 'fit';
    if (overall === 'unknown') overall = 'tight';
    var groups = [];
    var lines = [];
    var seen = {};
    var measured = {};
    var unknown = 0;
    var realIssue = false;
    var lean = { hall: false, outside: false, room: false, ceiling: false, thread: false };
    /* Why the route is a "talk to us": short of room, too close to
       call, or something we always look at ourselves. */
    var team = { short: 0, close: 0, policy: 0 };
    var how = { onend: false, tilt: false, thread: false, door: false, ceiling: false, doorHeight: false, close: false };
    var doorTips = [];

    results.forEach(function (r) {
      /* Spiral stairs are the team's whatever the numbers say. The
         engine only flags the shape once it has a stair width, so with
         the width left blank the page reads the stop the same way. */
      if (spiral && r.stop === 'stairs' && r.tier === 'unknown') {
        r = { id: r.id, stop: r.stop, kind: r.kind, tier: 'team', method: null, spare: null, shortBy: null, data: {}, flags: ['spiral'] };
      }
      var tier = TIER_RANK[r.tier] != null ? r.tier : 'team';
      var flags = r.flags || [];
      var words = describe(r, ctx);
      var tag = words.typical ? TYPICAL_TAG : '';
      var line = {
        id: r.id, stop: r.stop, tier: tier, label: words.label + tag, short: words.short + tag, text: words.text,
        typical: words.typical, brief: shortFor(r, ctx)
      };
      lines.push(line);
      measured[r.id] = true;
      if (!seen[r.stop]) {
        seen[r.stop] = { stop: r.stop, title: STOP_TITLES[r.stop] || 'On the way', lines: [] };
        groups.push(seen[r.stop]);
      }
      seen[r.stop].lines.push(line);

      /* Never better than the engine's roll-up, only ever as bad. */
      overall = worse(overall, tier === 'unknown' ? 'tight' : tier);
      if (tier === 'unknown') unknown++;
      else if (tier !== 'fit') realIssue = true;
      if (tier === 'team') {
        if (has(flags, 'too-close')) team.close++;
        else if (has(flags, 'spiral') || has(flags, 'unknown-shape')) team.policy++;
        else team.short++;
      }
      if (tier === 'onend') {
        if (r.method === 'thread') how.thread = true;
        else if (r.method === 'tilt') how.tilt = true;
        else how.onend = true;
        if (r.id === 'front-outside' || r.id === 'front-turn' || r.id === 'room-turn') how.door = true;
        if (has(flags, 'needs-ceiling')) how.ceiling = true;
        if (words.needsDoor) how.doorHeight = true;
        if (has(flags, 'very-tight')) how.close = true;
      }

      var turnOnTheFlat = r.id === 'front-turn' || r.id === 'hall-corner' || (r.id === 'room-turn' && !values.stairs);
      if (r.method === 'flat' && turnOnTheFlat && !typed(values, 'hallLen')) lean.hall = true;
      if (r.method === 'flat' && r.id === 'front-outside') lean.outside = true;
      if (r.id === 'room-turn' && r.method) lean.room = true;
      if (r.method === 'onend' || r.method === 'lean' || r.method === 'tilt') lean.ceiling = true;
      if (r.method === 'thread') lean.thread = true;

      if ((r.id === 'front' || r.id === 'room') && r.method !== 'open') {
        var gap = isNum(r.shortBy) ? r.shortBy : (r.data || {}).shortBy;
        if (tier === 'tight' || (tier === 'team' && (has(flags, 'too-close') || (isNum(gap) && gap < DOOR_OFF[u])))) {
          doorTips.push(r.id === 'front' ? 'the front door' : 'the room door');
        }
      }
    });

    var missing = [];
    if (!measured.front) missing.push('the front door');
    if (!measured.room) missing.push('the room door');
    var notChecked = missing.length
      ? 'We have not checked ' + listOf(missing) + ' yet, so add ' + (missing.length > 1 ? 'those' : 'that') + ' in step two for a full answer.'
      : '';
    var many = typicalStops.length > 1;
    var typicalNote = soft
      ? capital(listOf(typicalStops)) + (many ? ' are typical sizes, not your own measurements' : ' is a typical size, not your own measurement') +
        '. Homes vary, so measure ' + (many ? 'them' : 'it') + ' in step two for a firm answer.'
      : '';
    function notes() {
      return [typicalNote, notChecked].filter(function (s) { return !!s; }).join(' ');
    }
    var clears = (soft ? 'Every point checked' : 'Every point you have measured') + ' clears our safety margin of ' + m;

    var view = {
      empty: false, tier: overall, groups: groups, lines: lines, missing: missing, typical: typicalStops, note: '',
      recap: 'Checked for: ' + recapOf(info) + '.'
    };

    if (overall === 'fit' && !missing.length && !soft) {
      view.badge = 'It fits';
      view.head = 'Good news, it goes.';
      view.body = clears + ', carried the ordinary way. Our delivery team will treat it gently all the same.';
    } else if (overall === 'fit' && soft) {
      /* A fit that stands on a typical door or hallway is a good sign,
         not an answer about this house. */
      view.badge = 'Looking good';
      view.head = 'It should go, based on typical sizes.';
      view.body = clears + '. ' + notes();
    } else if (overall === 'fit') {
      /* Nothing measured has failed, but a door has not been measured
         at all, so the headline must not say it fits. */
      view.badge = 'So far, so good';
      view.head = 'Everything measured so far clears.';
      view.body = clears + '. ' + notChecked;
    } else if (overall === 'tight' && !realIssue && unknown > 0) {
      view.badge = 'Nearly there';
      view.head = 'So far so good, with a little more to measure.';
      view.body = (soft ? 'Everything checked so far' : 'Everything you have measured') + ' clears our safety margin of ' + m + ', but there ' +
        (unknown === 1 ? 'is one point' : 'are ' + unknown + ' points') +
        ' on the route we could not check. ' + (unknown === 1 ? 'It is' : 'They are') +
        ' marked below, with what to measure. Add ' + (unknown === 1 ? 'it' : 'them') +
        ' for a full answer, or send us what you have and the team will pick it up from there.';
      view.note = notes();
    } else if (overall === 'tight') {
      view.badge = 'Close, let us check';
      view.head = 'It should fit, but it is close.';
      view.body = 'At least one point on the route is inside our ' + m + ' safety margin, or is something we always look at ourselves. Every sofa is made by hand and can vary a little, so send us your measurements and we will confirm before anything is made.';
      view.note = notes();
    } else if (overall === 'onend') {
      /* The ordinary way most sofas reach most rooms, so it is said as
         that, and still as "likely": the sums cannot see a radiator. */
      view.badge = 'Likely, check with us';
      var parts = [];
      if (how.onend || how.tilt) {
        var carried = how.onend ? 'stood on its end' : 'tipped up on one end';
        view.head = 'It should go in, ' + carried + '.';
        parts.push('At one point or more the ' + piece + ' is ' + (piece === 'armchair' ? 'too big' : 'too long') + ' to turn lying flat' +
          (how.door ? '. That is true of most sofas at most doors off a hallway, so it is carried in ' : ', so it is carried through ') +
          carried + ' instead, which our delivery teams do often.');
      }
      if (how.thread) {
        if (!view.head) view.head = 'It should go in, with the legs fed through.';
        parts.push('The legs on this ' + piece + ' stay on, so at one doorway or more the body goes through first and the legs are fed through after it, one pair at a time.');
      }
      if (!view.head) view.head = 'Likely to fit, with careful handling.';
      /* A headline must not lean on a ceiling nobody has measured, or
         gloss over a carry the engine calls very tight. */
      if (how.ceiling || how.doorHeight) {
        view.head = 'Likely to fit, once the ' + (how.ceiling ? 'ceiling' : 'door height') + ' is checked.';
        parts.push(how.ceiling && how.doorHeight
          ? 'It does depend on a ceiling height and a door height we do not have yet, which are marked below.'
          : 'It does depend on a ' + (how.ceiling ? 'ceiling' : 'door') + ' height we do not have yet, which is marked below.');
      } else if (how.close) {
        view.head = 'Likely to fit, but it is very close.';
      }
      parts.push((soft ? 'By these figures' : 'By your measurements') + ' it should go, but our sums cannot see radiators, light fittings or a door left on its hinges, so please check with the team before you order.');
      view.body = parts.join(' ');
      view.note = notes();
    } else if (team.short || !(team.close || team.policy)) {
      view.badge = 'Talk to us';
      view.head = 'This one needs a proper look.';
      view.body = 'At least one point on the route is tighter than our usual carries allow. That is not the end of the story: a split frame, another way in or a door off its hinges often solves it. Send us a short video and we will work it out with you.';
      view.note = notes();
    } else if (team.close && !team.policy) {
      view.badge = 'Talk to us';
      view.head = 'This one is too close to call.';
      view.body = 'By the numbers the ' + piece + ' goes, but with under ' + showExact(floorOf(u), u) + ' to spare at one point or more. Every frame is made by hand and can vary a little, so we would rather look at it with you than guess. Send us a short video and we will work it out.';
      view.note = notes();
    } else {
      view.badge = 'Talk to us';
      view.head = 'This one needs a proper look.';
      view.body = 'Nothing here is ruled out, but part of this route is something we always look at ourselves before we give an answer. Send us a short video and we will work it out with you.';
      view.note = notes();
    }

    /* Only the assumptions this answer actually leaned on. */
    view.assumed = [];
    var sofaLen = isNum(ctx.sofa.L) ? ' (' + show(ctx.sofa.L, u) + ')' : '';
    if (lean.outside) {
      view.assumed.push('The corridor or passage outside the front door runs at least the length of the ' + piece + sofaLen + ', so there is room to line it up for the turn.');
    }
    if (lean.hall) {
      view.assumed.push('The hallway is at least as long as the ' + piece + sofaLen + ', so there is room to line it up for the turn.');
    }
    if (lean.room) {
      view.assumed.push('There is clear floor inside the room, about the length of the ' + piece + ', to straighten up once it is through the door.');
    }
    if (lean.ceiling) {
      view.assumed.push('Where the ' + piece + ' is stood up, the ceiling is clear of pendant lights, beams and loft hatches.');
    }
    if (lean.thread) {
      view.assumed.push('Where the legs are fed through, the wall and door frame are shallow enough to sit between the front legs and the back legs.');
    }

    /* Split frame and detachable arms. */
    var name = info.model ? 'The ' + info.model : 'This sofa';
    var hard = overall === 'onend' || overall === 'team';
    var split = result.split;
    view.extras = [];
    view.split = '';
    if (split && TIER_RANK[split.overall] != null && TIER_RANK[split.overall] < TIER_RANK[overall]) {
      var halves = name + ' can be made with a split frame: two halves' +
        (isNum(split.halfLen) ? ' of about ' + show(split.halfLen, u) + ' each' : '') + ', joined in the room. ';
      if (split.overall === 'fit') halves += 'As two halves, ' + (soft ? 'every point checked' : 'every point you have measured') + ' clears our safety margin.';
      else if (split.overall === 'tight') halves += 'As two halves it should fit, though at least one point is still close or still to be checked.';
      else halves += 'As two halves it is likely to go in, with careful handling.';
      view.split = halves + ' Split frames are only available on loose cover builds, so ask the team to confirm it suits the sofa you have in mind.';
    } else if (split && hard) {
      view.extras.push('A split frame, where the sofa arrives in two halves' +
        (isNum(split.halfLen) ? ' of about ' + show(split.halfLen, u) + ' each' : '') +
        ', does not change the answer on these measurements, but the team may still find a way. Split frames are only available on loose cover builds.');
    } else if (hard && !info.splitAvailable && info.onePiece) {
      view.extras.push('An armchair is made in one piece, so a split frame is not an option here, but get in touch and the team may still find a way.');
    } else if (hard && !info.splitAvailable) {
      view.extras.push(name + ' is not usually available with a split frame, but get in touch and we may still be able to accommodate.');
    }
    if (info.detachable && hard) {
      view.extras.push(name + ' can also be made with detachable arms for tight access. Mention it when you get in touch.');
    }

    /* Figures the customer has typed over our own. */
    view.bespoke = info.bespoke
      ? 'This answer is for bespoke dimensions, not our standard figures' + (info.model ? ' for the ' + info.model : '') +
        '. Please ask the team to confirm the finished size before you rely on it.'
      : '';

    view.tip = doorTips.length
      ? 'A tip: lifting a door off its hinges usually gains about ' + show(DOOR_OFF[u], u) + ', which could make all the difference at ' + listOf(doorTips) + '.'
      : '';
    view.video = overall !== 'fit'
      ? 'A short video of the route, from the front door to the room, is the quickest way for us to check. It is the first thing the team will ask for.'
      : '';
    return view;
  }

  /* ---- the email ---- */

  function exact(v) { return String(Math.round(v * 100) / 100); }

  var ENTRY_WORDS = {
    frontEntry: { ahead: 'hall straight ahead', side: 'wall facing, hall to one side', room: 'opens straight into the room' },
    frontOutside: { open: 'open outside', side: 'turn in from a corridor or passage' },
    stairShape: { straight: 'straight', quarter: 'quarter turn with a landing', half: 'half landing', winder: 'winding steps', spiral: 'spiral' },
    stairFoot: { ahead: 'reached straight on', turn: 'turn onto them' },
    stairTop: { ahead: 'straight on at the top', turn: 'turn at the top' },
    stairSide: { walls: 'walls both sides', banister: 'open banister' },
    roomEntry: { ahead: 'straight ahead', side: 'in a side wall' }
  };

  /* Every measurement the customer entered, as typed, one line a stop.
     Kept terse on purpose: each character here is up to three in the
     link, and the link has a ceiling. */
  function measureLines(v) {
    var out = [];
    function num(key, after, before) {
      return typed(v, key) ? (before ? before + ' ' : '') + exact(v[key]) + (after ? ' ' + after : '') : '';
    }
    /* A door or a lift opening: "78 x 198", or whichever half was given. */
    function opening(wKey, hKey) {
      if (typed(v, wKey) && typed(v, hKey)) return exact(v[wKey]) + ' x ' + exact(v[hKey]);
      return num(wKey, 'wide') || num(hKey, 'high');
    }
    function word(key) { return v[key] ? ENTRY_WORDS[key][v[key]] : ''; }
    /* A figure from a "closest match" is marked, so the team does not
       take a typical door for one the customer measured. */
    var preset = v.preset || {};
    function typical(name) {
      return PRESET_KEYS[name].some(function (k) { return !!preset[k] && typed(v, k); }) ? 'typical size, not measured' : '';
    }
    function row(title, bits) {
      bits = bits.filter(function (bit) { return !!bit; });
      if (bits.length) out.push(title + ': ' + bits.join(', '));
      return bits.length;
    }
    var noHall = v.frontEntry === 'room';

    row('Front door', [opening('frontW', 'frontH'), typical('front'), word('frontEntry'), word('frontOutside'),
      v.frontOutside === 'side' ? num('outsideW', 'wide outside') : '', v.frontOutside === 'side' ? num('outsideH', '', 'ceiling outside') : '',
      v.outsideSteps ? 'steps or tight path outside' : '']);
    if (!noHall) {
      row('Hallway', [num('hallW', 'wide'), num('hallPinch', '', 'pinch point'), num('hallH', '', 'ceiling'), typical('hall'),
        num('hallLen', '', 'length'), v.corner ? (num('hallW2', 'after the corner') || 'corner, not measured') : '']);
    }
    if (v.lift) {
      var door = opening('liftDoorW', 'liftDoorH');
      row('Lift', [door ? 'door ' + door : '', num('liftW', 'wide inside'), num('liftD', 'deep'), num('liftH', 'high')]) ||
        out.push('Lift: on the route, not measured');
    }
    if (v.stairs) {
      row('Stairs', [word('stairShape') || 'shape not given', num('stairW', 'wide'), num('stairH', '', 'headroom'), typical('stair'), word('stairFoot'),
        num('landingD', 'deep', 'landing'), num('landingW', 'wide', 'landing'), num('landingC', '', 'landing ceiling'),
        word('stairTop'), num('topD', '', 'depth at the top'), num('topC', '', 'ceiling at the top'), word('stairSide')]);
      row('Upstairs', [num('upW', 'wide'), num('upH', '', 'ceiling')]);
    }
    if (v.roomOpen) out.push('Room door: no door to go through');
    else row('Room door', [opening('roomW', 'roomH'), typical('room'), word('roomEntry'), num('roomC', '', 'room ceiling')]);
    return out;
  }

  /* Typographic characters cost six or more characters each once
     encoded, so the email is kept to plain ASCII. */
  /* Commas and colons are legal as they stand in a mailto link, and
     leaving them be saves two characters apiece. */
  function encode(s) {
    return encodeURIComponent(ascii(s)).replace(/%2C/g, ',').replace(/%3A/g, ':');
  }

  function ascii(s) {
    return String(s).replace(/\u00A0/g, ' ').replace(/[\u2018\u2019]/g, '\'').replace(/\u00D7/g, 'x');
  }

  /* The "Email the team" link. Result lines are dropped, and said to be
     dropped, until the whole link is short enough for a mail client to
     accept. The plain passes go first, and the lines the team most
     needs to see (talk to us, then on its end) go last. */
  function mailto(to, view, values, info) {
    values = values || {};
    info = info || {};
    var typedIn = values.inputUnit || values.unit || UNIT;
    var top = [
      'Hello,',
      '',
      'I have used the Will it fit? checker and would like the team to look at my route.',
      '',
      'Sofa: ' + (info.name || 'Custom sofa') + (info.market ? ' (' + info.market + ')' : '')
    ];
    /* The same recap the answer opens with: what was checked, and
       whether the legs were on. */
    if (info.dims) top.push('Checked at ' + info.dims + ', ' + (info.legs || (info.legsOff ? 'with the legs off' : 'with the legs on')) + '.');
    top.push('Legs can be removed: ' + (info.legsOff ? 'yes' : 'no') + '. Split frame: ' + (info.splitAvailable ? 'yes' : 'no') + '.');
    if (info.bespoke) top.push('Bespoke dimensions, not our standard figures: please confirm.');
    var measures = measureLines(values);
    if (measures.length) top = top.concat(['', 'My measurements (' + typedIn + '):'], measures);
    top = top.concat(['', 'The checker said: ' + view.badge]);

    var kept = view.lines.slice();
    var cut = false;
    var href = '';
    for (;;) {
      var body = top.concat(kept.map(function (l) { return l.brief; }), cut ? ['(More in the checker.)'] : []).join('\r\n');
      href = 'mailto:' + to + '?subject=' + encode('Access check: ' + (info.name || 'Custom sofa')) + '&body=' + encode(body);
      if (href.length <= URL_LIMIT || !kept.length) break;
      var drop = 0;
      for (var i = 1; i < kept.length; i++) {
        if ((TIER_RANK[kept[i].tier] || 0) <= (TIER_RANK[kept[drop].tier] || 0)) drop = i;
      }
      kept.splice(drop, 1);
      cut = true;
    }
    return href;
  }

  /* ============ 6. MARKET + STATE ============ */

  function byId(id) { return (doc && doc.getElementById) ? doc.getElementById(id) : null; }
  function one(sel, scope) {
    var s = scope || doc;
    return (s && s.querySelector) ? s.querySelector(sel) : null;
  }
  function all(sel, scope) {
    var s = scope || doc;
    var list = (s && s.querySelectorAll) ? s.querySelectorAll(sel) : null;
    return list ? Array.prototype.slice.call(list) : [];
  }

  function detectMarket() {
    /* Theme convention: the explicit data-sc-market attribute only,
       which the section sets from the storefront's market. Anything
       other than 'US' is UK. */
    var el = byId('sc-fc-card') || one('[data-sc-market]');
    return (el && el.getAttribute && el.getAttribute('data-sc-market') === 'US') ? 'US' : 'UK';
  }

  var MARKET_KEY = detectMarket();
  var UNIT = MARKET_UNIT[MARKET_KEY];
  var SETTINGS = { ranges: FC.ranges, hideSizes: FC.hideSizes };
  var FEED = SC.fitDimensions || null;
  var MODELS = buildModels(FEED, MARKET_KEY, SETTINGS);

  /* The sofa half is pickModel's shape (see 3b), with no range chosen
     yet. The rest belongs to the route form. */
  function freshState() {
    var s = pickModel(null);
    s.modelKey = null;         /* a range key, or 'custom' */
    s.step = 1;
    s.inputUnit = UNIT;        /* what the route inputs are typed in */
    s.roomOpen = false;        /* "no door to go through" chosen */
    s.oddSeen = '';            /* odd-looking numbers already queried once */
    return s;
  }
  var STATE = freshState();

  /* The figure behind each route input and the unit it was given in,
     so switching units back and forth cannot drift a number. */
  var RAW = {};
  /* The route inputs a "closest match" preset filled in and the
     customer has not typed over. Those are typical sizes, and the
     answer must never call them measurements. */
  var SRC = {};

  var card, grid, sizeRow, sizeChips, dimsBox, toStep2, shell, errorZone;

  function getModel(key) { return findByKey(MODELS, key); }
  function setSofa(patch) {
    for (var k in patch) if (Object.prototype.hasOwnProperty.call(patch, k)) STATE[k] = patch[k];
  }

  /* An answer on show belongs to the sofa and the route it was worked
     out from. The moment either changes it is thrown away, and step
     three works it out again on the way back in. */
  function clearResult() {
    if (shell) shell.innerHTML = '';
  }

  /* ============ 7. STEP 1: sofa grid, sizes, dims editor ============ */

  function renderGrid() {
    if (!grid) return;
    var html = '';
    MODELS.forEach(function (m) {
      html += '<button type="button" class="sc-fc-sofa-tile' + (m.key === STATE.modelKey ? ' is-selected' : '') +
        '" aria-pressed="' + (m.key === STATE.modelKey ? 'true' : 'false') + '" data-model="' + esc(m.key) + '">' +
        '<span class="sc-fc-tile-name">' + esc(m.name) + '</span>' +
        '<span class="sc-fc-tile-meta">' + m.sizes.length + (m.sizes.length === 1 ? ' size' : ' sizes') + '</span>' +
        '</button>';
    });
    html += '<button type="button" class="sc-fc-sofa-tile' + (STATE.modelKey === 'custom' ? ' is-selected' : '') +
      '" aria-pressed="' + (STATE.modelKey === 'custom' ? 'true' : 'false') + '" data-model="custom">' +
      '<span class="sc-fc-tile-name">Custom / other</span>' +
      '<span class="sc-fc-tile-meta">enter your own</span></button>';
    grid.innerHTML = html;
  }

  /* Selection is marked in place rather than by a repaint, so keyboard
     focus stays on the button that was pressed. */
  function markPicked(scope, sel, attr, key) {
    all(sel, scope).forEach(function (el) {
      var on = el.getAttribute(attr) === key;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function sofaChanged() {
    validateStep1();
    clearResult();
  }

  function selectModel(key) {
    var m = getModel(key);
    /* A clean slate every time: no size, no figures and a disabled
       Continue button until a size is picked or figures are typed. */
    setSofa(pickModel(m));
    markPicked(grid, '.sc-fc-sofa-tile', 'data-model', STATE.modelKey);
    if (m) {
      renderSizeChips(m);
      if (sizeRow) sizeRow.classList.add('is-active');
      if (dimsBox) { dimsBox.innerHTML = ''; dimsBox.style.display = 'none'; }
    } else {
      /* Custom: no size chips, straight to a blank dims editor. */
      if (sizeRow) sizeRow.classList.remove('is-active');
      if (sizeChips) sizeChips.innerHTML = '';
      renderDims(null);
      if (dimsBox) dimsBox.style.display = 'block';
    }
    sofaChanged();
  }

  function renderSizeChips(m) {
    if (!sizeChips) return;
    var html = '';
    m.sizes.forEach(function (s) {
      html += '<button type="button" class="sc-fc-size-chip' + (s.key === STATE.sizeKey ? ' is-selected' : '') +
        '" aria-pressed="' + (s.key === STATE.sizeKey ? 'true' : 'false') + '" data-size="' + esc(s.key) + '">' + esc(s.label) + '</button>';
    });
    html += '<button type="button" class="sc-fc-size-chip' + (STATE.sizeKey === 'bespoke' ? ' is-selected' : '') +
      '" aria-pressed="' + (STATE.sizeKey === 'bespoke' ? 'true' : 'false') + '" data-size="bespoke">Bespoke size</button>';
    sizeChips.innerHTML = html;
  }

  function selectSize(sizeKey) {
    var m = getModel(STATE.modelKey);
    if (!m) return;
    setSofa(pickSize(m, sizeKey));
    markPicked(sizeChips, '.sc-fc-size-chip', 'data-size', sizeKey);
    renderDims(m);
    if (dimsBox) dimsBox.style.display = 'block';
    sofaChanged();
  }

  function dimField(label, key, val, hint) {
    var id = 'sc-fc-dim-' + key;
    return '<div class="sc-fc-field"><label for="' + id + '">' + label +
      (hint ? ' <span class="sc-fc-field-hint">' + hint + '</span>' : '') + '</label>' +
      '<span class="sc-fc-input"><input id="' + id + '" type="number" inputmode="decimal" min="0" step="any" data-dim="' + key +
      '" value="' + (val == null ? '' : plain(val)) + '">' +
      '<span class="suffix">' + UNIT + '</span></span></div>';
  }

  function renderDims(m) {
    if (!dimsBox) return;
    var d = STATE.dims;
    var notes = '';
    if (m) {
      var name = esc(m.name);
      if (!m.legsRemovable) notes += '<p class="sc-fc-flag-note">The ' + name + ' does not usually have removable legs, but get in touch and we may still be able to accommodate.</p>';
      if (!m.split) notes += '<p class="sc-fc-flag-note">The ' + name + ' is not usually available with a split frame, but get in touch and we may still be able to accommodate.</p>';
      else if (!splitFor(m, STATE.sizeKey)) notes += '<p class="sc-fc-flag-note">An armchair is made in one piece, so there is no split frame for this size.</p>';
      if (m.detachable) notes += '<p class="sc-fc-flag-note">The ' + name + ' can be made with detachable arms for tight access. Mention it when you get in touch.</p>';
    }
    dimsBox.innerHTML =
      '<div class="sc-fc-dims-head">Your sofa\u2019s measurements <span class="sc-fc-dims-sub">' +
      (m ? 'Filled in from our standard specifications. Ordering bespoke? Adjust any of them.' : 'Enter the width, height and depth, and the rest if you know them. The more you give us, the better the answer.') +
      '</span></div>' +
      '<div class="sc-fc-dims-grid">' +
      dimField('External width', 'w', d.w) +
      dimField('External height', 'h', d.h) +
      dimField('External depth', 'd', d.d) +
      dimField('Access height', 'access', d.access, 'height with the legs off') +
      dimField('Leg height', 'feet', d.feet, m ? 'floor to the underside of the frame' : 'optional') +
      '</div>' +
      '<div class="sc-fc-dims-notes" id="sc-fc-dims-notes" aria-live="polite"></div>' +
      '<div class="sc-fc-flags">' +
      '<label class="sc-fc-flag"><input type="checkbox" data-flag="legs"' + (STATE.legsRemovable ? ' checked' : '') + '> Legs can be removed</label>' +
      '<label class="sc-fc-flag"><input type="checkbox" data-flag="split"' + (STATE.split ? ' checked' : '') + '> Split frame available</label>' +
      '</div>' +
      notes +
      '<p class="sc-fc-flag-note">Split frames are only available on loose cover builds.</p>';
    renderDimNotes();
  }

  /* What the figures need said about them, kept apart from the inputs
     so it can change as the customer types without moving the cursor. */
  function renderDimNotes() {
    var box = byId('sc-fc-dims-notes');
    if (!box) return;
    var n = sofaNotes(STATE, UNIT);
    var html = '';
    n.errors.forEach(function (t) { html += '<p class="sc-fc-flag-note sc-fc-dims-error">' + esc(t) + '</p>'; });
    n.notes.forEach(function (t) { html += '<p class="sc-fc-flag-note sc-fc-dims-query">' + esc(t) + '</p>'; });
    box.innerHTML = html;
  }

  /* Steps two and three open only for a sofa with a width, a height
     and a depth, all above nought. */
  function step1Ok() { return sofaNotes(STATE, UNIT).ok; }

  function validateStep1() {
    if (toStep2) toStep2.disabled = !step1Ok();
  }

  function wireStep1() {
    if (grid) grid.addEventListener('click', function (e) {
      var tile = e.target.closest('.sc-fc-sofa-tile');
      if (tile) selectModel(tile.getAttribute('data-model'));
    });
    if (sizeChips) sizeChips.addEventListener('click', function (e) {
      var chip = e.target.closest('.sc-fc-size-chip');
      if (chip) selectSize(chip.getAttribute('data-size'));
    });
    if (!dimsBox) return;
    dimsBox.addEventListener('input', function (e) {
      var key = e.target.getAttribute('data-dim');
      if (!key) return;
      setSofa(editDim(STATE, key, e.target.value));
      /* The access height follows the frame height until the customer
         types one of their own. */
      if (key === 'h' && !STATE.touched.access) {
        var a = one('input[data-dim="access"]', dimsBox);
        if (a) a.value = STATE.dims.access == null ? '' : plain(STATE.dims.access);
      }
      renderDimNotes();
      sofaChanged();
    });
    dimsBox.addEventListener('change', function (e) {
      var flag = e.target.getAttribute('data-flag');
      if (!flag) return;
      if (flag === 'legs') STATE.legsRemovable = e.target.checked;
      else STATE.split = e.target.checked;
      STATE.flagsEdited = true;
      sofaChanged();
    });
  }

  /* ============ 8. STEP 2: presets, units, choices ============ */

  function field(key) { return one('input[type="number"][data-key="' + key + '"]', card); }
  function checkbox(key) { return one('input[type="checkbox"][data-key="' + key + '"]', card); }
  function group(key) { return one('.sc-fc-choice[data-key="' + key + '"]', card); }

  /* An input counts only while it is on show. A field tucked away by a
     toggle or a choice is treated as blank, so nothing the customer
     cannot see is ever checked or quoted back to them. */
  function isShown(el) {
    while (el && el !== card) {
      if (el.style && el.style.display === 'none') return false;
      el = el.parentNode;
    }
    return true;
  }

  function choiceValue(key) {
    var g = group(key);
    if (!g || !isShown(g)) return null;
    var on = one('.sc-fc-choice-chip[aria-checked="true"]', g);
    return on ? on.getAttribute('data-value') : null;
  }

  function isChecked(key) {
    var c = checkbox(key);
    return !!(c && c.checked && isShown(c));
  }

  function shapeValue() {
    var sel = one('select[data-key="stairShape"]', card);
    return (sel && sel.value && isShown(sel)) ? sel.value : null;
  }

  function readValues() {
    var v = { unit: UNIT, inputUnit: STATE.inputUnit, preset: {} };
    NUM_KEYS.forEach(function (key) {
      var i = field(key);
      var x = (i && i.value !== '' && isShown(i)) ? parseFloat(i.value) : NaN;
      /* Kept as typed, nought and all. Everything that reads these
         treats nought or less as blank, and oddValues tells the
         customer so: it is a slip of the finger, not an open plan. */
      v[key] = isNaN(x) ? null : x;
      if (SRC[key] && x > 0) v.preset[key] = true;
    });
    CHECK_KEYS.forEach(function (key) { v[key] = isChecked(key); });
    CHOICE_KEYS.forEach(function (key) { v[key] = choiceValue(key); });
    v.stairShape = shapeValue();
    v.roomOpen = !!STATE.roomOpen;
    if (v.roomOpen) {
      v.roomW = null;
      v.roomH = null;
      v.roomC = null;
      v.roomEntry = null;
    }
    return v;
  }

  /* Elements carrying data-when="key=a,b" (or key!=a) show only while
     the form is in that state. One rule, so the markup says what
     depends on what. */
  function syncVisibility() {
    var st = {
      corner: isCheckedRaw('corner'), lift: isCheckedRaw('lift'), stairs: isCheckedRaw('stairs'),
      frontEntry: rawChoice('frontEntry'), frontOutside: rawChoice('frontOutside'), stairTop: rawChoice('stairTop'),
      stairShape: (one('select[data-key="stairShape"]', card) || {}).value || '',
      room: STATE.roomOpen ? 'none' : 'door'
    };
    all('[data-when]', card).forEach(function (el) {
      var rule = el.getAttribute('data-when');
      var not = rule.indexOf('!=') !== -1;
      var bits = rule.split(not ? '!=' : '=');
      var hit = has(bits[1].split(','), String(st[bits[0]] == null ? '' : st[bits[0]]));
      el.style.display = (not ? !hit : hit) ? '' : 'none';
    });
  }
  function isCheckedRaw(key) { var c = checkbox(key); return (c && c.checked) ? '1' : '0'; }
  function rawChoice(key) {
    var on = one('.sc-fc-choice[data-key="' + key + '"] .sc-fc-choice-chip[aria-checked="true"]', card);
    return on ? on.getAttribute('data-value') : '';
  }

  /* Write one route figure. The figure and the unit it was given in
     are kept, and the input shows it in the unit now selected. */
  function setField(key, v, unit, fromPreset) {
    var i = field(key);
    if (!i) return;
    delete SRC[key];
    if (!isNum(v)) { i.value = ''; delete RAW[key]; return; }
    RAW[key] = { v: v, u: unit };
    if (fromPreset) SRC[key] = true;
    i.value = shown(v, unit, STATE.inputUnit);
  }

  /* The "closest match" lists. Painted again whenever the unit
     changes, so a label always speaks the unit on the fields. */
  function paintPresets() {
    ['front', 'hall', 'stair', 'room'].forEach(function (key) {
      var sel = one('select[data-preset="' + key + '"]', card);
      if (!sel) return;
      var keep = sel.value;
      var html = '<option value="">Choose</option>';
      PRESETS[MARKET_KEY][key].forEach(function (o) {
        html += '<option value="' + o[0] + '">' + esc(presetLabel(o[1], o[0], UNIT, STATE.inputUnit)) + '</option>';
      });
      if (key === 'hall') html += '<option value="none">No hallway: the front door opens straight into a room</option>';
      if (key === 'room') html += '<option value="none">No door to go through (open plan, or the front door opens into this room)</option>';
      html += '<option value="custom">I will measure it myself</option>';
      sel.innerHTML = html;
      sel.value = keep;
    });
  }

  function onPreset(sel) {
    var key = sel.getAttribute('data-preset');
    var box = one('[data-custom-for="' + key + '"]', card);
    var val = sel.value;
    if (key === 'hall' && val === 'none') {
      /* "No hallway" is the same answer as "it opens straight into the
         room", so it is held in that one place: never as a width of
         nought, which a slip of the finger could also type. */
      sel.value = '';
      if (box) box.style.display = 'none';
      var chip = one('.sc-fc-choice[data-key="frontEntry"] .sc-fc-choice-chip[data-value="room"]', card);
      if (chip) pickChoice(chip);
      return;
    }
    if (key === 'room') STATE.roomOpen = (val === 'none');
    if (val === '' || val === 'none') {
      if (box) box.style.display = 'none';
    } else {
      if (val === 'custom') {
        /* Measuring it themselves: a typical figure they have not
           typed over is cleared, so it cannot pass as their own. */
        PRESET_KEYS[key].forEach(function (k) { if (SRC[k]) setField(k, null); });
      } else {
        /* Presets are held in the market's unit and written in the
           unit now selected, or a customer who switched to inches
           first would get centimetre figures in fields marked inches. */
        var parts = val.split(',');
        PRESET_KEYS[key].forEach(function (k, idx) { setField(k, parseFloat(parts[idx]), UNIT, true); });
      }
      /* The figures a preset fills in stay on show and editable. */
      if (box) box.style.display = '';
    }
    syncVisibility();
    clearResult();
  }

  function setUnit(u, fresh) {
    var old = STATE.inputUnit;
    if (!fresh && u !== old) {
      /* Convert what is already typed, in place. */
      all('input[type="number"][data-key]', card).forEach(function (i) {
        var key = i.getAttribute('data-key');
        if (i.value === '') { delete RAW[key]; return; }
        var cur = parseFloat(i.value);
        if (isNaN(cur)) return;
        var raw = RAW[key];
        if (!raw || parseFloat(shown(raw.v, raw.u, old)) !== cur) raw = RAW[key] = { v: cur, u: old };
        i.value = shown(raw.v, raw.u, u);
      });
    }
    STATE.inputUnit = u;
    all('.sc-fc-unit-btn', card).forEach(function (b) {
      var on = b.getAttribute('data-unit') === u;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    all('[data-suffix]', card).forEach(function (s) { s.textContent = u; });
    paintPresets();
    if (!fresh) clearResult();
  }

  function pickChoice(chip) {
    var g = chip.parentNode;
    all('.sc-fc-choice-chip', g).forEach(function (c) {
      var on = c === chip;
      c.classList.toggle('is-selected', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    g.classList.remove('is-missing');
    syncVisibility();
    clearResult();
  }

  function wireStep2() {
    all('select[data-preset]', card).forEach(function (sel) {
      sel.addEventListener('change', function () { onPreset(sel); });
    });
    all('.sc-fc-unit-btn', card).forEach(function (btn) {
      btn.addEventListener('click', function () { setUnit(btn.getAttribute('data-unit')); });
    });
    card.addEventListener('input', function (e) {
      var key = e.target.getAttribute && e.target.getAttribute('data-key');
      if (!key || e.target.type !== 'number') return;
      var v = parseFloat(e.target.value);
      if (isNaN(v)) delete RAW[key];
      else RAW[key] = { v: v, u: STATE.inputUnit };
      /* Typed over: it is the customer's figure now, not a typical one. */
      delete SRC[key];
      clearResult();
    });
    card.addEventListener('change', function (e) {
      var t = e.target;
      if (!t.getAttribute || !t.getAttribute('data-key')) return;
      if (t.type === 'checkbox' || t.tagName === 'SELECT') {
        if (t.parentNode && t.parentNode.classList) t.parentNode.classList.remove('is-missing');
        syncVisibility();
        clearResult();
      }
    });
    card.addEventListener('click', function (e) {
      var chip = e.target.closest('.sc-fc-choice-chip');
      if (chip) pickChoice(chip);
    });
    /* Arrow keys move through a choice group, as they do through radios. */
    card.addEventListener('keydown', function (e) {
      var chip = e.target.closest ? e.target.closest('.sc-fc-choice-chip') : null;
      if (!chip) return;
      var move = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0);
      if (!move) return;
      var chips = all('.sc-fc-choice-chip', chip.parentNode);
      var next = chips[(chips.indexOf(chip) + move + chips.length) % chips.length];
      if (!next) return;
      e.preventDefault();
      next.focus();
      pickChoice(next);
    });
    setUnit(UNIT, true);
    resync();
  }

  /* Bring what is on show into line with the controls. Run at start-up
     and again on pageshow, because a browser restoring the page (back
     button, reload) can hand it over with boxes already ticked and a
     preset already chosen. */
  function resync() {
    all('select[data-preset]', card).forEach(function (sel) {
      var key = sel.getAttribute('data-preset');
      var box = one('[data-custom-for="' + key + '"]', card);
      var val = sel.value;
      if (key === 'room') STATE.roomOpen = (val === 'none');
      if (box) box.style.display = (val === '' || val === 'none') ? 'none' : '';
      /* A restored preset whose figures are still the preset's own is
         still a typical size. */
      if (val && val !== 'none' && val !== 'custom') {
        var fill = presetFill(MARKET_KEY, key, val, STATE.inputUnit);
        PRESET_KEYS[key].forEach(function (k) {
          var i = field(k);
          if (i && i.value !== '' && parseFloat(i.value) === fill[k]) SRC[k] = true;
        });
      }
    });
    syncVisibility();
  }

  /* ---- problems with the route form ---- */

  function clearProblems() {
    if (errorZone) errorZone.innerHTML = '';
    all('.is-missing', card).forEach(function (el) { el.classList.remove('is-missing'); });
  }

  function showProblems(list, soft) {
    clearProblems();
    if (!errorZone) return;
    var html = '<div class="sc-fc-inline-error" role="alert"><p>' +
      (soft ? 'One or two of these numbers look unusual. Please check them, and that the unit at the top (centimetres or inches) matches your tape.'
            : 'A little more to tell us before we can check:') + '</p><ul>';
    list.forEach(function (p) { html += '<li>' + esc(p.text) + '</li>'; });
    html += '</ul>' + (soft ? '<p>If they are right, press Check it again and we will carry on.</p>' : '') + '</div>';
    errorZone.innerHTML = html;

    var first = null;
    list.forEach(function (p) {
      var el = group(p.key) || (one('select[data-key="' + p.key + '"]', card) || {}).parentNode;
      if (!el || !el.classList) return;
      el.classList.add('is-missing');
      if (!first) first = el;
    });
    /* A missing choice is flagged where it sits, so go there. A number
       that looks odd is explained in the box above, so go to the box. */
    var target = (!soft && first) ? first : errorZone;
    if (target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /* ============ 9. STEP 3: the answer ============ */

  function plainResult(head, body) {
    return '<div class="sc-fc-result sc-fc-result-team"><h3>' + head + '</h3><p>' + body + '</p></div>';
  }

  /* The answer panel, as markup. Pure: a view from summarise() and the
     email link in, a string out. */
  function resultHtml(view, href) {
    var h = '<div class="sc-fc-result sc-fc-result-' + view.tier + '">';
    /* What the answer is based on comes first, so nobody reads a
       verdict for a sofa they did not mean. */
    if (view.recap) h += '<p class="sc-fc-checked">' + esc(view.recap) + '</p>';
    h += '<span class="sc-fc-badge">' + esc(view.badge) + '</span>' +
      '<h3>' + esc(view.head) + '</h3>' +
      '<p>' + esc(view.body) + '</p>';
    if (view.note) h += '<p>' + esc(view.note) + '</p>';

    view.groups.forEach(function (g) {
      h += '<div class="sc-fc-group"><h4 class="sc-fc-group-h">' + esc(g.title) + '</h4><ul class="sc-fc-detail">';
      g.lines.forEach(function (l) {
        h += '<li class="sc-fc-line sc-fc-line-' + l.tier + '"><strong>' + esc(l.short) + ':</strong> ' + esc(l.text) + '</li>';
      });
      h += '</ul></div>';
    });

    if (view.split) h += '<p class="sc-fc-extra sc-fc-split">' + esc(view.split) + '</p>';
    (view.extras || []).forEach(function (x) { h += '<p class="sc-fc-extra">' + esc(x) + '</p>'; });
    if (view.bespoke) h += '<p class="sc-fc-extra">' + esc(view.bespoke) + '</p>';
    if (view.tip) h += '<p class="sc-fc-extra sc-fc-tip">' + esc(view.tip) + '</p>';

    if (view.assumed && view.assumed.length) {
      h += '<div class="sc-fc-assumed"><h4 class="sc-fc-group-h">What we have assumed</h4><ul>';
      view.assumed.forEach(function (a) { h += '<li>' + esc(a) + '</li>'; });
      h += '</ul></div>';
    }
    if (view.video) h += '<p class="sc-fc-video">' + esc(view.video) + '</p>';

    h += '<div class="sc-fc-result-ctas">' +
      '<a class="sc-btn sc-btn-primary" href="' + esc(href) + '">Email the team</a>' +
      '<a class="sc-btn sc-btn-text" href="#" data-sc-nadia-open>Or talk to Nadia now</a>' +
      '</div></div>';
    return h;
  }

  /* Sofa + route -> the whole answer: the engine's result, the view,
     the email link and the markup. Pure, so node can run a sofa through
     a house exactly as the page does. Null when the engine is missing.
     opts = { market, to }. */
  function answer(sofa, model, values, opts) {
    opts = opts || {};
    values = values || {};
    var engine = SC.fitEngine;
    if (!engine || typeof engine.evaluate !== 'function') return null;
    var unit = values.unit || UNIT;
    var result = engine.evaluate({ unit: unit, sofa: engineSofaOf(sofa), checks: buildChecks(values) });
    var info = infoOf(sofa, model, opts.market || MARKET_KEY, unit, result);
    var view = summarise(result, values, info);
    var out = { result: result, info: info, view: view, href: '', html: '' };
    if (view.empty) return out;
    out.href = mailto(opts.to || 'sofas@sophieconran.com', view, values, info);
    out.html = resultHtml(view, out.href);
    return out;
  }

  function renderResult(values) {
    if (!shell) return;
    var to = FC.contactEmail || 'sofas@sophieconran.com';
    var out = null;
    try {
      out = answer(STATE, getModel(STATE.modelKey), values, { market: MARKET_KEY, to: to });
    } catch (e) {
      if (root.console && console.warn) console.warn('Will it fit: the check could not be run.', e);
    }
    if (!out) {
      shell.innerHTML = plainResult('We could not run the check',
        'Something went wrong on our side, not yours. Please refresh the page and try again, or <a href="mailto:' + esc(to) +
        '">email the team</a> with your measurements and we will check them for you.');
      return;
    }
    if (out.view.empty) {
      shell.innerHTML = plainResult('Nothing to check yet',
        'Pop at least one measurement into step two and we will give you an honest answer.');
      return;
    }
    shell.innerHTML = out.html;
  }

  /* "Check it", and a click on the third step: both come through here,
     so the answer on show always belongs to the sofa and the route as
     they stand now. With no valid sofa there is never a verdict. */
  function check() {
    if (!step1Ok()) { clearResult(); goToStep(1); return; }
    var values = readValues();
    var problems = validateRoute(values);
    var soft = false;
    if (!problems.length) {
      var odd = oddValues(values);
      var mark = odd.map(function (p) { return p.key + '=' + values[p.key] + values.inputUnit; }).join('|');
      if (odd.length && mark !== STATE.oddSeen) {
        STATE.oddSeen = mark;
        problems = odd;
        soft = true;
      }
    }
    if (problems.length) {
      clearResult();
      if (STATE.step !== 2) goToStep(2);
      showProblems(problems, soft);
      return;
    }
    clearProblems();
    renderResult(values);
    goToStep(3);
  }

  /* ============ 10. STEPPER / NAV ============ */

  function goToStep(n) {
    n = parseInt(n, 10) || 1;
    STATE.step = n;
    all('.sc-fc-step', card).forEach(function (s) {
      var k = parseInt(s.getAttribute('data-step'), 10);
      s.classList.toggle('is-active', k === n);
      s.classList.toggle('is-complete', k < n);
      s.setAttribute('aria-selected', k === n ? 'true' : 'false');
    });
    all('.sc-fc-panel', card).forEach(function (p) {
      p.classList.toggle('is-active', p.getAttribute('data-panel') === String(n));
    });
    if (card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* Later steps are only reachable with a sofa chosen: a check with no
     sofa has nothing to squeeze through any door, and reads as a fit. */
  function requestStep(n) {
    n = parseInt(n, 10) || 1;
    if (n <= 1 || !step1Ok()) { goToStep(1); return; }
    if (n === 2) { goToStep(2); return; }
    check();
  }

  /* Everything back to how the page loaded: the sofa, its flags, the
     route, the toggles, the choices, the unit and the answer. */
  function resetAll() {
    STATE = freshState();
    RAW = {};
    SRC = {};
    all('input[data-key]', card).forEach(function (i) {
      if (i.type === 'checkbox') i.checked = false;
      else i.value = '';
    });
    all('select[data-preset], select[data-key]', card).forEach(function (s) { s.value = ''; });
    all('.sc-fc-choice-chip', card).forEach(function (c) {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
    });
    all('[data-custom-for]', card).forEach(function (el) { el.style.display = 'none'; });
    setUnit(UNIT, true);
    syncVisibility();
    clearProblems();
    clearResult();
    renderGrid();
    if (sizeRow) sizeRow.classList.remove('is-active');
    if (sizeChips) sizeChips.innerHTML = '';
    if (dimsBox) { dimsBox.innerHTML = ''; dimsBox.style.display = 'none'; }
    validateStep1();
    goToStep(1);
  }

  function wireNav() {
    if (toStep2) toStep2.addEventListener('click', function () { requestStep(2); });
    var to3 = byId('sc-fc-to-3');
    if (to3) to3.addEventListener('click', check);
    all('[data-back]', card).forEach(function (b) {
      b.addEventListener('click', function () { goToStep(b.getAttribute('data-back')); });
    });
    var reset = byId('sc-fc-reset');
    if (reset) reset.addEventListener('click', resetAll);
    all('.sc-fc-step', card).forEach(function (s) {
      s.addEventListener('click', function () { requestStep(s.getAttribute('data-step')); });
    });
  }

  /* ============ 11. LIVE DIMENSIONS ============ */
  /* The copy bundled in the theme is the first paint and the fallback.
     The live feed is the SSOT sheet as the dimensions Worker serves it,
     so a corrected figure is on the page within minutes and needs no
     theme push. Mirrors SC.renderRoom.loadFromManifest: if the fetch
     fails, is unconfigured, or matches what is already on screen, the
     bundled data simply stays. */

  var warned = false;
  function warnOnce(e) {
    if (warned) return;
    warned = true;
    if (root.console && console.warn) console.warn('Will it fit: live dimensions unavailable, using the bundled copy.', e);
  }

  /* Swap in a merged feed. Returns true when anything on show changed. */
  function applyFeed(feed) {
    var before = fingerprint(MODELS);
    FEED = feed;
    MODELS = buildModels(FEED, MARKET_KEY, SETTINGS);
    if (fingerprint(MODELS) === before) return false;
    if (!card) return true;

    var picked = STATE.modelKey && STATE.modelKey !== 'custom';
    var m = getModel(STATE.modelKey);
    if (picked && !m) {
      /* The range has gone from the list. Keep the numbers the customer
         was working with, as a sofa of their own. */
      STATE.modelKey = 'custom';
      STATE.sizeKey = null;
      STATE.base = null;
      if (sizeRow) sizeRow.classList.remove('is-active');
    }
    renderGrid();
    if (picked && m) {
      /* Keep the selection. Refresh the figures quietly, unless the
         customer has typed over them: then their numbers stand. */
      var s = findByKey(m.sizes, STATE.sizeKey);
      if (STATE.sizeKey && STATE.sizeKey !== 'bespoke' && !s) STATE.sizeKey = 'bespoke';
      var ticked = STATE.flagsEdited ? { legsRemovable: STATE.legsRemovable, split: STATE.split } : null;
      if (s && !STATE.dimsEdited) setSofa(pickSize(m, s.key));
      /* Their own numbers stand, but "standard" now means the new figures. */
      else if (s) STATE.base = pickSize(m, s.key).base;
      if (ticked) {
        /* Flags the customer has set themselves are theirs to keep. */
        STATE.legsRemovable = ticked.legsRemovable;
        STATE.split = ticked.split;
        STATE.flagsEdited = true;
      } else {
        STATE.legsRemovable = m.legsRemovable;
        STATE.split = splitFor(m, STATE.sizeKey);
      }
      renderSizeChips(m);
      if (STATE.sizeKey && !STATE.dimsEdited) renderDims(m);
    }
    validateStep1();
    /* An answer already on show was worked out from the old figures. */
    if (STATE.step === 3 && step1Ok()) renderResult(readValues());
    return true;
  }

  function loadDimensions(url) {
    if (!url || typeof root.fetch !== 'function') return null;
    return root.fetch(url).then(function (r) {
      if (!r.ok) throw new Error('dimensions ' + r.status);
      return r.json();
    }).then(function (live) {
      var why = feedProblem(live, MARKET_KEY);
      if (why) throw new Error(why);
      return applyFeed(mergeFeed(FEED, live, MARKET_KEY));
    })['catch'](function (e) {
      warnOnce(e);
      return false;
    });
  }

  /* ============ 12. TEST SURFACE ============ */
  /* Pure functions only: no DOM, no state. Node loads this file with a
     stub document whose lookups return null and calls these directly. */
  FC._test = {
    buildChecks: buildChecks,
    buildModels: buildModels,
    mergeFeed: mergeFeed,
    lineFor: lineFor,
    summarise: summarise,
    mailto: mailto,
    validateRoute: validateRoute,
    oddValues: oddValues,
    feedProblem: feedProblem,
    sizeKeyOf: sizeKeyOf,
    presets: PRESETS,
    presetLabel: presetLabel,
    presetFill: presetFill,
    pickModel: pickModel,
    pickSize: pickSize,
    editDim: editDim,
    sofaNotes: sofaNotes,
    engineSofaOf: engineSofaOf,
    infoOf: infoOf,
    resultHtml: resultHtml,
    answer: answer
  };

  /* ============ INIT ============ */
  function init() {
    card = byId('sc-fc-card');
    if (!card) return;
    grid = byId('sc-fc-sofa-grid');
    sizeRow = byId('sc-fc-size-row');
    sizeChips = byId('sc-fc-size-chips');
    dimsBox = byId('sc-fc-dims');
    toStep2 = byId('sc-fc-to-2');
    shell = byId('sc-fc-result-shell');
    errorZone = byId('sc-fc-error-zone');

    /* 1) Paint at once from the bundled copy. */
    renderGrid();
    wireStep1();
    wireStep2();
    wireNav();
    validateStep1();

    if (root.addEventListener) root.addEventListener('pageshow', resync);

    /* 2) Then the live feed, repainting only if a figure changed. */
    loadDimensions(FC.dimensionsUrl);
  }

  if (doc) {
    if (doc.readyState === 'loading' && doc.addEventListener) doc.addEventListener('DOMContentLoaded', init);
    else init();
  }
})();
